import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { SubscriptionPlanOffcanvas } from '../components/SubscriptionPlanOffcanvas';
import { BookingBasket } from '../components/BookingBasket';
import { ConfirmModal } from '../components/ConfirmModal';
import ExpectationPhotos from '../components/ExpectationPhotos';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import { ukSlotToISO, ukNow } from '../../lib/uk-time';
import {
  customerApiErrorMessage,
  getMyWallet,
  startMembershipCheckout,
} from '../../lib/customer-api';
import { toast } from '../../lib/toast';
import {
  getBookingDraft,
  patchBookingDraft,
  setBookingDraft,
} from '../../lib/booking-draft';
import {
  listAddons,
  listAvailablePros,
  listCategories,
  listNearestAvailablePros,
  listServices,
  type AvailablePro,
  type ServiceAddonRow,
  type ServiceCategory,
  type ServiceItem,
} from '../../lib/booking-api';

// Fallback category code if the draft has no category set (e.g. the
// user navigated to /booking directly without going through the
// landing dropdown). Was the only supported code before this page
// became category-aware.
const FALLBACK_CATEGORY_CODE = 'CLEANING';

// Minimum notice for SAME-DAY bookings (minutes). A start time on today's
// date is only offered if it's at least this far ahead of now — mirrors the
// backend's SAME_DAY_LEAD_MIN so the wizard's time grid (incl. the local
// fallback grid) never offers a past / too-soon slot. e.g. booking at 12:00
// → earliest 14:00.
const SAME_DAY_LEAD_MIN = 120;

// The ONLY service that shows the "Want to save on this booking?" £15/mo
// membership upsell (updated requirement: Standard Cleaning only). An explicit
// allowlist on the resolved service code — the premium cleans (deep /
// end-of-tenancy / commercial) and every step where no service is resolved yet
// fall through to "hidden". `CLEAN_STANDARD` is the seed code for Standard
// Cleaning (services.code).
const MEMBERSHIP_UPSELL_SERVICE_CODE = 'CLEAN_STANDARD';

// Recurring (subscription) cadences. 'daily' bills + visits EVERY day; the
// others repeat on a chosen weekday. WEEKDAY_FREQUENCIES is the subset that
// needs the "which day works best?" step (7) and weekday-restricts the start
// calendar — 'daily' has no single weekday, so it's recurring but NOT a
// weekday frequency. Ids mirror the category flow_config + the admin editor's
// FREQUENCY_ALLOWED_IDS allowlist.
const RECURRING_FREQUENCIES = new Set(['daily', 'weekly', 'biweekly', 'monthly']);
const WEEKDAY_FREQUENCIES = new Set(['weekly', 'biweekly', 'monthly']);
const isRecurringFreq = (f: string | null | undefined): boolean =>
  !!f && RECURRING_FREQUENCIES.has(f);
const isWeekdayFreq = (f: string | null | undefined): boolean =>
  !!f && WEEKDAY_FREQUENCIES.has(f);

// Parse a duration-option id ("3h", "3h30", "210min", "2.5") into a
// number of hours. Mirrors the basket's parser so the recommended-hours
// lookup (which matches a property-size recommendation to a duration
// button) speaks the same units the admin configured.
const durationIdToHours = (id: string): number => {
  if (!id) return 0;
  const minsOnly = /^(\d+)\s*min$/i.exec(id);
  if (minsOnly) return parseInt(minsOnly[1], 10) / 60;
  const hAndM = /^(\d+)h(\d+)?$/i.exec(id);
  if (hAndM) {
    const h = parseInt(hAndM[1], 10);
    const m = hAndM[2] ? parseInt(hAndM[2], 10) : 0;
    return h + m / 60;
  }
  const num = parseFloat(id);
  return Number.isFinite(num) ? num : 0;
};

export default function BookingFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Catalog service id the customer has selected (resolved from the
  // /public/services list, not a wizard-internal slug). Null until
  // the catalog finishes loading + the first service is auto-picked.
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
    null,
  );
  // Resume on the step the customer was last on — when they navigate
  // forward to /secondary-preferred or /your-appointment then click
  // Back, we restore the wizard rather than dropping them at step 1.
  // Location state (an explicit override from another route) wins
  // over the draft, which wins over the default of 1.
  const [sessionStep, setSessionStep] = useState<number>(() => {
    const fromState = (location.state as any)?.sessionStep;
    if (typeof fromState === 'number' && fromState >= 1 && fromState <= 12) {
      return fromState;
    }
    const fromDraft = getBookingDraft()?.meta?.last_session_step;
    if (typeof fromDraft === 'number' && fromDraft >= 1 && fromDraft <= 12) {
      return fromDraft;
    }
    return 1;
  });

  useEffect(() => {
    if ((location.state as any)?.sessionStep) {
      setSessionStep((location.state as any).sessionStep);
    }
  }, [location.state]);

  // Mirror sessionStep into the booking draft so Back from any
  // downstream page resumes the wizard at the same step. Only persist
  // once the customer has actually progressed (step > 1) — we don't
  // want a stale step-1 to leak across a fresh booking session.
  useEffect(() => {
    if (sessionStep <= 1) return;
    const draft = getBookingDraft();
    const nextMeta = {
      ...(draft?.meta ?? {}),
      last_session_step: sessionStep,
    };
    if (draft) {
      setBookingDraft({ ...draft, meta: nextMeta });
    } else {
      patchBookingDraft({ meta: nextMeta });
    }
  }, [sessionStep]);

  // Each wizard step is a new "screen" within the same /booking route, so
  // the route-level <ScrollRestoration> can't reset scroll between steps.
  // Jump to the top on every step change so a long step (e.g. add-ons)
  // doesn't leave the next one scrolled half-way down.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [sessionStep]);

  // Lazy initialisers hydrate from the persisted draft so a refresh
  // mid-wizard (step 7, 8, anywhere) doesn't wipe the customer's
  // earlier picks. Defaults are still '' / null when the draft has
  // nothing for that key. Catalog-dependent invariants (e.g. "this
  // duration id has to exist in the active category's flow_config")
  // are re-asserted by the catalog-load effect once it lands, which
  // also default-picks the first option when the saved value is stale.
  const [selectedSpace, setSelectedSpace] = useState<string>(
    () => (getBookingDraft()?.meta?.space as string) ?? '',
  );
  const [selectedDuration, setSelectedDuration] = useState<string>(
    () => (getBookingDraft()?.meta?.duration_id as string) ?? '',
  );
  // True once the customer taps a specific duration on step 3 — their
  // explicit pick then wins over the property-size recommendation.
  // Seeded true when a saved duration is restored so a returning
  // customer keeps their choice; reset on a genuine property-size change
  // so the new size's recommendation takes effect.
  const [durationTouched, setDurationTouched] = useState<boolean>(
    () => !!getBookingDraft()?.meta?.duration_id,
  );
  const [hasPets, setHasPets] = useState<string>(
    () => (getBookingDraft()?.meta?.has_pets as string) ?? '',
  );
  const [notes, setNotes] = useState<string>(
    () => getBookingDraft()?.customer_note ?? '',
  );
  // Reference photos for "Your expectations concerning the pro".
  const [expectationImages, setExpectationImages] = useState<string[]>(
    () => getBookingDraft()?.customer_note_images ?? [],
  );
  const [frequency, setFrequency] = useState<string>(
    () => (getBookingDraft()?.meta?.frequency as string) ?? '',
  );
  const [selectedDay, setSelectedDay] = useState<string>(
    () => (getBookingDraft()?.meta?.day as string) ?? '',
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const ymdStr = getBookingDraft()?.meta?.date as string | undefined;
    if (!ymdStr) return null;
    // YYYY-MM-DD → midnight-local Date. new Date('YYYY-MM-DD') parses
    // as UTC which can land on the previous day in negative offsets,
    // so split the parts and pass them to the local constructor.
    const [y, m, d] = ymdStr.split('-').map((p) => parseInt(p, 10));
    if (!y || !m || !d) return null;
    const out = new Date(y, m - 1, d);
    return Number.isNaN(out.getTime()) ? null : out;
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(() => {
    const times = getBookingDraft()?.meta?.times as string[] | undefined;
    return Array.isArray(times) && times[0] ? times[0] : null;
  });
  const [selectedAddons, setSelectedAddons] = useState<{ id: string, duration: string }[]>(
    () => {
      const saved = getBookingDraft()?.meta?.addons;
      return Array.isArray(saved) ? (saved as { id: string; duration: string }[]) : [];
    },
  );
  const [addons, setAddons] = useState<ServiceAddonRow[]>([]);
  const [selectedProId, setSelectedProId] = useState<string | null>(
    () => getBookingDraft()?.professional_id ?? null,
  );
  // "Browse other pros" toggle on the recommendation screen (step 11).
  // The recommended pro shows first; the rest of the available pros stay
  // collapsed until the customer chooses to browse them. Kept in-wizard
  // (single-select + continue) so the session context — chosen hours,
  // frequency, schedule — survives picking a different pro.
  const [showOtherPros, setShowOtherPros] = useState(false);
  const [substitutePreference, setSubstitutePreference] = useState<string>(
    () => (getBookingDraft()?.meta?.substitute_preference as string) ?? 'yes',
  );
  const [isSubscriptionPlanOpen, setSubscriptionPlanOpen] = useState(false);
  // Guest-friendly subscribe gate: the offcanvas lists plans tied to the
  // signed-in account, so we surface a tiny "please sign in" prompt
  // before opening it for anonymous visitors.
  const { isAuthenticated: isCustomerAuthed } = useCustomerAuth();
  const [signInPromptOpen, setSignInPromptOpen] = useState(false);

  // Wallet — fetched for signed-in customers so the cleaning wizard's
  // basket shows the "Use my wallet balance" toggle too. It was
  // previously only wired up on /your-appointment, so cleaning (which
  // has its own in-wizard basket) appeared to be missing the wallet
  // while other categories showed it. The basket hides it automatically
  // for subscriptions; the chosen amount is persisted to the draft so
  // the final /your-appointment checkout picks it up.
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletCurrency, setWalletCurrency] = useState('GBP');
  const [walletAmount, setWalletAmount] = useState<number>(() =>
    Number(getBookingDraft()?.wallet_amount ?? 0),
  );
  const [walletEnabled, setWalletEnabled] = useState<boolean>(
    () => Number(getBookingDraft()?.wallet_amount ?? 0) > 0,
  );
  useEffect(() => {
    if (!isCustomerAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyWallet();
        if (cancelled) return;
        setWalletBalance(Number(res?.balance ?? 0));
        setWalletCurrency(res?.currency ?? 'GBP');
      } catch {
        /* non-blocking — basket just won't show the wallet toggle */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCustomerAuthed]);

  const handleSubscribeClick = () => {
    if (isCustomerAuthed) {
      setSubscriptionPlanOpen(true);
    } else {
      setSignInPromptOpen(true);
    }
  };
  // The offcanvas's "Pick Plan" button just no-ops without a handler.
  // Mirror BillingSubscriptionPage: kick off a Stripe membership
  // checkout and hand the customer over to the hosted page. Defaults
  // to monthly (the offcanvas doesn't expose a cycle toggle yet).
  const handlePickPlan = async (planId: string) => {
    // Close the offcanvas first so any subsequent error toast is
    // visible (the offcanvas otherwise covered the toast and the
    // button looked unresponsive).
    setSubscriptionPlanOpen(false);
    try {
      const res = await startMembershipCheckout({
        subscription_id: planId,
        subscription_type: 'MONTHLY',
      });
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not start checkout. Please try again.'),
      );
    }
  };
  const [selectedCommercialSpace, setSelectedCommercialSpace] = useState<string>(
    () => (getBookingDraft()?.meta?.commercial_space as string) ?? '',
  );
  const [otherSpaceDetails, setOtherSpaceDetails] = useState<string>(
    () => (getBookingDraft()?.meta?.commercial_space_other as string) ?? '',
  );
  // Commercial workspace size + override hours. The id is either a
  // sqft bracket (e.g. "medium") or a bedroom count for Airbnb (e.g.
  // "3"); both pull their recommended hours from flow_config so a
  // single state covers both flavours. `commercialHoursOverride`
  // stays null while the customer sticks with the recommendation;
  // when they tweak the hours-picker it captures the new value.
  const [selectedCommercialSize, setSelectedCommercialSize] = useState<string>(
    () => (getBookingDraft()?.meta?.commercial_size as string) ?? '',
  );
  const [commercialHoursOverride, setCommercialHoursOverride] = useState<
    number | null
  >(() => {
    const v = getBookingDraft()?.meta?.commercial_hours_override;
    return typeof v === 'number' ? v : null;
  });

  // Mirror every wizard selection into the draft on each change so a
  // refresh anywhere in the funnel restores exactly what the customer
  // had picked. Previously persistDraft only fired on Next, so picking
  // a date on step 8 and then F5 wiped the calendar back to nothing.
  useEffect(() => {
    const draft = getBookingDraft();
    const nextMeta = {
      ...(draft?.meta ?? {}),
      space: selectedSpace || null,
      duration_id: selectedDuration || null,
      has_pets: hasPets || null,
      frequency: frequency || null,
      day: selectedDay || null,
      date: selectedDate ? ymd(selectedDate) : null,
      times: selectedTimeSlot ? [selectedTimeSlot] : [],
      addons: selectedAddons,
      substitute_preference: substitutePreference || null,
      commercial_space: selectedCommercialSpace || null,
      commercial_space_other: otherSpaceDetails || null,
      commercial_size: selectedCommercialSize || null,
      commercial_hours_override: commercialHoursOverride,
    };
    patchBookingDraft({
      meta: nextMeta,
      professional_id: selectedProId ?? null,
      customer_note: notes || null,
      customer_note_images: expectationImages.length ? expectationImages : null,
    });
  }, [
    selectedSpace,
    selectedDuration,
    hasPets,
    notes,
    expectationImages,
    frequency,
    selectedDay,
    selectedDate,
    selectedTimeSlot,
    selectedAddons,
    selectedProId,
    substitutePreference,
    selectedCommercialSpace,
    otherSpaceDetails,
    selectedCommercialSize,
    commercialHoursOverride,
  ]);

  // ---------- Real availability ----------
  // Catalog + availability state. Loaded once we reach step 7 (first
  // step that depends on real data); kept around for steps 8 and 9.
  const [catalogReady, setCatalogReady] = useState(false);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [availablePros, setAvailablePros] = useState<AvailablePro[]>([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  // Calendar viewport — which month grid is rendered. Default to the
  // restored date's month after a refresh so step 8 opens on the
  // correct page instead of jumping back to "today" and hiding the
  // pre-selected day.
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const ymdStr = getBookingDraft()?.meta?.date as string | undefined;
    if (ymdStr) {
      const [y, m] = ymdStr.split('-').map((p) => parseInt(p, 10));
      if (y && m) return new Date(y, m - 1, 1);
    }
    const d = new Date();
    d.setDate(1);
    return d;
  });

  const toggleAddon = (id: string) => {
    setSelectedAddons(prev =>
      prev.find(a => a.id === id) ? prev.filter(a => a.id !== id) : [...prev, { id, duration: '30min' }]
    );
  };

  const updateAddonDuration = (id: string, duration: string) => {
    setSelectedAddons(prev =>
      prev.map(a => a.id === id ? { ...a, duration } : a)
    );
  };

  // ---------- Catalog loader ----------
  // Load categories + services + add-ons for whichever SESSION
  // category the customer picked on the landing page. The id is
  // read from the booking draft (set by LandingPage.handleBookNow).
  // If no draft category exists we fall back to the historical
  // CLEANING default so direct nav to /booking still works.
  useEffect(() => {
    (async () => {
      try {
        const [cats, svcs] = await Promise.all([
          listCategories(),
          listServices(),
        ]);
        setCategories(cats.data);
        setServices(svcs.data);
        const draft = getBookingDraft();
        const draftCat =
          (draft?.service_category_id &&
            cats.data.find((c) => c.id === draft.service_category_id)) ||
          null;
        const fallbackCat = cats.data.find(
          (c) => c.code === FALLBACK_CATEGORY_CODE,
        );
        // Always prefer a SESSION category — a TREATMENT one shouldn't
        // be running through this wizard. (RequireBookingAddress + the
        // landing routing usually prevent that.)
        const sessionCat =
          draftCat?.booking_flow === 'SESSION'
            ? draftCat
            : (fallbackCat ?? null);
        if (sessionCat) {
          try {
            const addonRes = await listAddons(sessionCat.id);
            setAddons(addonRes.data);
          } catch {
            // Add-ons failing isn't fatal — step 10 just shows
            // nothing rather than blocking checkout.
          }
        }
        // Restore the customer's last selection when they're returning
        // to the wizard (e.g. Back from /continue-booking). Otherwise
        // default to the first service so step 1 isn't empty.
        if (sessionCat) {
          const draftServiceId = draft?.service_id ?? null;
          const draftSvc =
            draftServiceId &&
            svcs.data.find(
              (s) =>
                s.id === draftServiceId &&
                s.category?.id === sessionCat.id,
            );
          const first = svcs.data
            .filter((s) => s.category?.id === sessionCat.id)
            .sort((a, b) => a.sort_order - b.sort_order)[0];
          const initial = draftSvc?.id ?? first?.id ?? null;
          if (initial) {
            setSelectedServiceId((prev) => prev ?? initial);
          }
        }
      } catch {
        // Catalog failure is non-fatal — surfaces as an empty step 1.
      } finally {
        setCatalogReady(true);
      }
    })();
  }, []);

  // Currently-picked service from the catalog. The whole wizard
  // hangs off this — duration, addons, "is this commercial?" all
  // read from it. `selectedServiceId` flows in from step 1.
  const resolvedService = useMemo(() => {
    if (!selectedServiceId || services.length === 0) return null;
    return services.find((s) => s.id === selectedServiceId) ?? null;
  }, [services, selectedServiceId]);

  // The active SESSION category for this wizard. Resolved from the
  // draft's service_category_id when present, otherwise the
  // legacy CLEANING fallback so direct nav to /booking still works.
  const flowCategory = useMemo<ServiceCategory | null>(() => {
    if (categories.length === 0) return null;
    const draft = getBookingDraft();
    const draftId = draft?.service_category_id ?? null;
    const draftCat = draftId
      ? (categories.find((c) => c.id === draftId) ?? null)
      : null;
    if (draftCat?.booking_flow === 'SESSION') return draftCat;
    return (
      categories.find((c) => c.code === FALLBACK_CATEGORY_CODE) ?? null
    );
  }, [categories]);

  // Services in the active category, sorted for the step 1 picker.
  const flowServices = useMemo(() => {
    if (!flowCategory) return [];
    return services
      .filter((s) => s.category?.id === flowCategory.id)
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [services, flowCategory]);

  // Heading for the free-text step (step 5) — the active category's own
  // TEXT question label (Cleaning: "Your expectations concerning the pro";
  // Childcare: "Anything we should know?…"). Falls back to the cleaning
  // wording for legacy configs without a TEXT question.
  const textQuestionLabel = useMemo(() => {
    const questions = (flowCategory?.flow_config?.questions ?? []) as Array<{
      type?: string;
      label?: string;
    }>;
    const textQ = questions.find((q) => q.type === 'TEXT');
    return textQ?.label || 'Your expectations concerning the pro';
  }, [flowCategory]);

  // A SESSION category with a single service (e.g. Childcare) has no
  // "sub-service" to choose — the lone service auto-selects and the
  // picker step (1) is dropped so the wizard opens on the first real
  // step. Categories with multiple services (Cleaning) keep the picker.
  // Gated on catalogReady so we never hide it mid-load (flowServices is
  // [] until the catalog arrives).
  const hideServicePicker = catalogReady && flowServices.length === 1;

  // Whether the picked service is the commercial one (drives the
  // commercial-vs-residential branch in handleNext + step 2). Hoisted
  // above stepCopy so its title can flip based on residential vs
  // commercial without a forward reference.
  const isCommercialService = useMemo(() => {
    return (
      (resolvedService?.properties?.applies_to ?? 'RESIDENTIAL') ===
      'COMMERCIAL'
    );
  }, [resolvedService]);

  // ---------- Step ordering (driven by flow_config.step_sequences) ----------
  // The wizard renders the same numbered step components for every
  // SESSION category; the ORDER and SUBSET each category shows comes
  // from its flow_config.step_sequences. Cleaning's sequences reproduce
  // the historical hardcoded order exactly (including the commercial
  // 2→6 jump). Childcare uses a slimmer DEFAULT sequence
  // (duration → schedule → requirements → pro). A category with no
  // step_sequences falls back to the legacy hardcoded order so nothing
  // changes for it.
  //
  // Token → step-number map (the step components themselves are
  // unchanged — we only choose which to visit and in what order):
  //   SPACE_SIZE / COMMERCIAL_SPACE / COMMERCIAL_SIZE → 2 (one screen)
  //   DURATION → 3   QUESTION:<YES_NO> → 4   QUESTION:<TEXT> → 5
  //   FREQUENCY → 6   SCHEDULE → 7 (day, recurring only) + 8 + 9
  //   ADDONS → 10
  // Step 1 (service picker) and step 11 (pro) bookend every sequence;
  // step 12 (backup pro) is appended at runtime for subscriptions.
  const activeSequence = useMemo<string[] | null>(() => {
    const seqs = flowCategory?.flow_config?.step_sequences;
    if (!seqs) return null;
    const appliesTo = resolvedService?.properties?.applies_to as
      | string
      | undefined;
    return (
      (appliesTo ? seqs[appliesTo] : undefined) ??
      seqs.DEFAULT ??
      seqs[Object.keys(seqs)[0]] ??
      null
    );
  }, [flowCategory, resolvedService]);

  const stepOrder = useMemo<number[]>(() => {
    // The "Which day works best?" step (7) is ONLY for weekday-recurring
    // bookings (weekly/biweekly/monthly) — those need a day-of-week. A
    // one-off (and a DAILY sub, which runs every day) goes straight to the
    // calendar (8 = date, 9 = time), so it must skip step 7 entirely.
    const chosenNeedsWeekday = isWeekdayFreq(frequency);
    // Legacy fallback — exactly the old hardcoded order.
    if (!activeSequence) {
      const legacy = isCommercialService
        ? [1, 2, 6, 7, 8, 9, 10, 11]
        : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
      const ordered = chosenNeedsWeekday
        ? legacy
        : legacy.filter((n) => n !== 7);
      return hideServicePicker ? ordered.filter((n) => n !== 1) : ordered;
    }
    const questions = flowCategory?.flow_config?.questions ?? [];
    const hasFrequency = activeSequence.includes('FREQUENCY');
    const order: number[] = [];
    const push = (n: number) => {
      if (!order.includes(n)) order.push(n);
    };
    // Service picker is first — unless the category has a single service
    // (e.g. Childcare), where it's skipped and the one service auto-selects.
    if (!hideServicePicker) push(1);
    for (const token of activeSequence) {
      if (
        token === 'SPACE_SIZE' ||
        token === 'COMMERCIAL_SPACE' ||
        token === 'COMMERCIAL_SIZE'
      ) {
        push(2);
      } else if (token === 'DURATION') {
        push(3);
      } else if (token === 'FREQUENCY') {
        push(6);
      } else if (token === 'SCHEDULE') {
        // Day-of-week only for weekday-recurring; one-off and daily jump
        // straight to the calendar (8 = date, 9 = time).
        if (hasFrequency && chosenNeedsWeekday) push(7);
        push(8);
        push(9);
      } else if (token === 'ADDONS') {
        push(10);
      } else if (token.startsWith('QUESTION:')) {
        const qid = token.slice('QUESTION:'.length);
        const q = questions.find((x) => x.id === qid);
        push(q?.type === 'YES_NO' ? 4 : 5);
      }
    }
    push(11); // pro recommendation always last
    return order;
  }, [
    activeSequence,
    isCommercialService,
    flowCategory,
    hideServicePicker,
    frequency,
  ]);

  // Single-service category (picker hidden): never sit on the hidden
  // step 1 — advance to the first real step once the catalog is ready
  // and the lone service has auto-selected.
  useEffect(() => {
    if (hideServicePicker && sessionStep === 1 && stepOrder.length > 0) {
      setSessionStep(stepOrder[0]);
    }
  }, [hideServicePicker, sessionStep, stepOrder]);

  // ---------- Flow-config-driven option lists ----------
  // Each step's options come straight from category.flow_config when
  // the admin has filled it in; otherwise we fall back to sensible
  // hard-coded defaults so a brand-new category still produces a
  // usable wizard.
  const spaceSizeOptions = useMemo(() => {
    const fromConfig = flowCategory?.flow_config?.space_sizes;
    if (fromConfig && fromConfig.length > 0) {
      // Admin-configured sizes may not carry recommended_hours (older
      // saves dropped it). Backfill a sensible default by position — 2h
      // for the smallest, +1h each step up — so the step-3 recommendation
      // always shows for every cleaning service, whatever the saved
      // config looks like. An explicit recommended_hours still wins.
      return fromConfig.map((o, i) => ({
        ...o,
        recommended_hours:
          typeof o.recommended_hours === 'number' && o.recommended_hours > 0
            ? o.recommended_hours
            : Math.min(2 + i, 8),
      }));
    }
    // Each size carries a recommended number of cleaning hours that
    // pre-fills + recommends a duration on step 3. Admin can override
    // these (incl. the hours) via flow_config.space_sizes.
    return [
      { id: 'studio', label: 'Studio / 1 bedroom', recommended_hours: 2 },
      { id: '2bed', label: '2 bedrooms', recommended_hours: 3 },
      { id: '3-4bed', label: '3 - 4 bedrooms', recommended_hours: 4 },
      { id: '5+bed', label: '5+ bedrooms', recommended_hours: 5 },
    ];
  }, [flowCategory]);

  const durationOptions = useMemo(() => {
    const fromConfig = flowCategory?.flow_config?.duration_hours;
    if (fromConfig && fromConfig.length > 0) return fromConfig;
    return ['2h', '2h30', '3h', '3h30', '4h', '4h30', '5h', '5h30', '6h', '7h'].map(
      (id) => ({ id, label: id }),
    );
  }, [flowCategory]);

  // Recommended cleaning hours for the chosen residential property size
  // (Studio/1-bed → 2h, 2-bed → 3h, 3-4-bed → 4h, …). Drives the
  // recommendation hint + pre-selection on the step-3 duration picker.
  const recommendedResidentialHours = useMemo<number | null>(() => {
    // Only flows that actually show a property-size step (residential
    // cleaning) get a size-based hours recommendation. Childcare and
    // commercial have no SPACE_SIZE step, so there's nothing to base one
    // on — and the "Based on your space (Studio…)" hint would be wrong.
    const hasSpaceSizeStep = activeSequence
      ? activeSequence.includes('SPACE_SIZE')
      : !isCommercialService;
    if (!hasSpaceSizeStep) return null;
    const opt = spaceSizeOptions.find((o) => o.id === selectedSpace);
    const hrs = opt?.recommended_hours;
    return typeof hrs === 'number' && hrs > 0 ? hrs : null;
  }, [activeSequence, isCommercialService, spaceSizeOptions, selectedSpace]);

  // Map that recommendation onto a real duration-option id. Prefers an
  // exact hours match, else the smallest option that still covers it,
  // else the longest available — so the highlighted button always
  // exists in the current (possibly admin-customised) options list.
  const recommendedDurationId = useMemo<string | null>(() => {
    if (recommendedResidentialHours == null || durationOptions.length === 0)
      return null;
    const exact = durationOptions.find(
      (o) => durationIdToHours(o.id) === recommendedResidentialHours,
    );
    if (exact) return exact.id;
    const sorted = [...durationOptions].sort(
      (a, b) => durationIdToHours(a.id) - durationIdToHours(b.id),
    );
    const covering = sorted.find(
      (o) => durationIdToHours(o.id) >= recommendedResidentialHours,
    );
    return (covering ?? sorted[sorted.length - 1])?.id ?? null;
  }, [durationOptions, recommendedResidentialHours]);

  // Human label for the picked property size — used in the step-3
  // recommendation hint ("Based on your space (2 bedrooms)…").
  const selectedSpaceLabel = useMemo<string>(
    () => spaceSizeOptions.find((o) => o.id === selectedSpace)?.label ?? '',
    [spaceSizeOptions, selectedSpace],
  );

  const commercialSpaceOptions = useMemo(() => {
    const fromConfig = flowCategory?.flow_config?.commercial_spaces;
    if (fromConfig && fromConfig.length > 0) return fromConfig;
    return [
      { id: 'office', label: 'Office / Workspace' },
      { id: 'holiday', label: 'Holiday Rental / Airbnb' },
      { id: 'retail', label: 'Retail / Hospitality' },
      { id: 'nursery', label: 'Nursery / Childcare Setting' },
      { id: 'gym', label: 'Gym / Wellness' },
      { id: 'other', label: 'Other (please specify)' },
    ];
  }, [flowCategory]);

  // True when the customer picked an Airbnb / holiday-rental space —
  // those use a bedroom count instead of sq ft. Driven by the
  // category's `airbnb_space_ids` (defaults to ['holiday']) so admin
  // can rename / add ids without a code change.
  const isAirbnbCommercialSpace = useMemo(() => {
    const ids =
      flowCategory?.flow_config?.airbnb_space_ids ?? ['holiday'];
    return ids.includes(selectedCommercialSpace);
  }, [flowCategory, selectedCommercialSpace]);

  // Workspace-size options shown after the commercial space type
  // picker. Sq ft brackets for offices/retail/etc.; bedroom counts
  // for Airbnb. Each option carries its own recommended_hours, which
  // pre-fills the duration step (customer can still override).
  const commercialSizeOptions = useMemo(() => {
    const fc = flowCategory?.flow_config;
    // Admin-configured sizes may not carry recommended_hours (older
    // config, or rows added before the field existed). Backfill a
    // sensible value by position — smaller spaces first — so the
    // "Recommended Xh" badge and duration pre-fill always work. An
    // explicit recommended_hours always wins.
    const withHours = <T extends { recommended_hours?: number }>(opts: T[]) =>
      opts.map((o, i) => ({
        ...o,
        recommended_hours:
          typeof o.recommended_hours === 'number' && o.recommended_hours > 0
            ? o.recommended_hours
            : Math.min(2 + i, 8),
      }));
    if (isAirbnbCommercialSpace && fc?.airbnb_bedroom_options?.length) {
      return withHours(fc.airbnb_bedroom_options);
    }
    if (fc?.commercial_size_options?.length) {
      return withHours(fc.commercial_size_options);
    }
    // Sensible fallback so a brand-new category with no flow_config
    // still renders something usable.
    return isAirbnbCommercialSpace
      ? [
          { id: '1', label: '1 bedroom', recommended_hours: 2 },
          { id: '2', label: '2 bedrooms', recommended_hours: 3 },
          { id: '3', label: '3 bedrooms', recommended_hours: 4 },
          { id: '4', label: '4 bedrooms', recommended_hours: 5 },
          { id: '5+', label: '5+ bedrooms', recommended_hours: 6 },
        ]
      : [
          { id: 'small', label: 'Up to 500 sq ft', recommended_hours: 2 },
          { id: 'medium', label: '500 – 1,000 sq ft', recommended_hours: 3 },
          { id: 'large', label: '1,000 – 2,000 sq ft', recommended_hours: 4 },
          { id: 'xlarge', label: '2,000 – 4,000 sq ft', recommended_hours: 6 },
          { id: 'xxlarge', label: '4,000+ sq ft', recommended_hours: 8 },
        ];
  }, [flowCategory, isAirbnbCommercialSpace]);

  // Effective hours for a commercial booking — manual override wins,
  // otherwise the picked size's recommended hours, otherwise 2 as a
  // floor so the booking never goes through with 0 hours.
  const commercialEffectiveHours = useMemo(() => {
    if (commercialHoursOverride !== null && commercialHoursOverride > 0)
      return commercialHoursOverride;
    const opt = commercialSizeOptions.find(
      (o) => o.id === selectedCommercialSize,
    );
    if (opt) return opt.recommended_hours;
    return 2;
  }, [commercialHoursOverride, commercialSizeOptions, selectedCommercialSize]);

  // Frequency options for step 6. Falls back to a sensible default
  // if the admin hasn't filled the category's flow_config yet.
  const frequencyOptions = useMemo(() => {
    const fromConfig = flowCategory?.flow_config?.frequencies;
    if (fromConfig && fromConfig.length > 0) return fromConfig;
    return [
      { id: 'weekly', label: 'Weekly' },
      { id: 'biweekly', label: 'Biweekly' },
      { id: 'monthly', label: 'Monthly' },
      { id: 'oneoff', label: 'One-off' },
    ];
  }, [flowCategory]);

  // Step 7 weekday picker. We keep the English day-name as the
  // canonical *id* (the persistDraft code maps "Monday" → 1 for the
  // backend's day_of_week) but render the locale-formatted name to
  // the user. 2024-01-01 was a Monday, so we generate Mon → Sun off
  // that anchor.
  const weekdayOptions = useMemo(() => {
    // Format in UTC so the Monday-Sunday anchor doesn't drift in
    // timezones west of UTC (where a UTC-Monday could render as
    // Sunday locally).
    const englishFmt = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      timeZone: 'UTC',
    });
    const localeFmt = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      timeZone: 'UTC',
    });
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(Date.UTC(2024, 0, 1 + i)); // 2024-01-01 = Monday
      return {
        id: englishFmt.format(d), // stable English name for dayMap
        label: localeFmt.format(d),
      };
    });
  }, []);

  // Seed each picker from the first option of its admin-managed list
  // once that list has loaded. State stays empty until then so we
  // never carry a stale hardcoded id like '3-4bed' / '3h' / 'weekly'
  // that may not exist in the active category's flow_config.
  useEffect(() => {
    if (spaceSizeOptions.length === 0) return;
    const stillValid = spaceSizeOptions.some((o) => o.id === selectedSpace);
    if (!stillValid) setSelectedSpace(spaceSizeOptions[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceSizeOptions]);

  useEffect(() => {
    if (durationOptions.length === 0) return;
    // The customer's explicit pick ALWAYS wins. Once they've tapped a
    // duration — or one was restored from the saved draft, which seeds
    // durationTouched=true — never auto-change it. The previous extra
    // `&& stillValid` guard meant that while the category's configured
    // duration options were still loading (or briefly used a different id
    // set), a hand-picked "3h30" counted as "not in the list" and got
    // yanked back to the property-size recommendation (e.g. 2h) on
    // back-navigation / remount. Respecting the touched flag
    // unconditionally fixes that — the duration shown is always the one the
    // customer chose.
    if (durationTouched) return;
    // Untouched: pin the picker to the property-size recommendation when
    // there is one; else keep a still-valid value, else the first option.
    // This reinstates the "recommended hours based on property size"
    // behaviour on a fresh "How many hours?" step.
    const stillValid = durationOptions.some((o) => o.id === selectedDuration);
    const next =
      recommendedDurationId ??
      (stillValid ? selectedDuration : durationOptions[0].id);
    if (next && next !== selectedDuration) setSelectedDuration(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationOptions, recommendedDurationId, durationTouched]);

  // NOTE: re-arming the recommended-hours default on a property-size change
  // is handled directly in the size-picker click handler (step 2), NOT in a
  // useEffect on `selectedSpace`. An effect also fired when `selectedSpace`
  // was restored from the saved draft on back-navigation / remount, which
  // wiped the "customer hand-picked" flag and reverted a chosen 3.5h back to
  // the size's recommended default. Tying it to the actual click keeps a
  // restored duration locked in.

  useEffect(() => {
    // A category whose flow doesn't include the FREQUENCY step is always
    // one-off (e.g. Childcare). Without this guard the picker defaulted
    // to the first option ("Weekly"), silently turning the booking into a
    // subscription even though the customer never saw a frequency step.
    // (activeSequence === null = legacy cleaning, which keeps recurring.)
    if (activeSequence && !activeSequence.includes('FREQUENCY')) {
      if (frequency !== 'oneoff') setFrequency('oneoff');
      return;
    }
    if (frequencyOptions.length === 0) return;
    const stillValid = frequencyOptions.some((o) => o.id === frequency);
    if (!stillValid) {
      // Default to one-off when the category offers it. Defaulting to the
      // first option ("Weekly" for Cleaning) silently made every cleaning
      // booking a recurring subscription out of the gate — which routes to
      // the subscription checkout and HIDES the wallet/voucher (Stripe
      // Subscriptions bill the full per-cycle amount). Recurring is still
      // one tap away in the frequency step. Falls back to the first option
      // for any category that doesn't offer a one-off.
      const oneoff = frequencyOptions.find((o) => o.id === 'oneoff');
      setFrequency(oneoff ? oneoff.id : frequencyOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequencyOptions, activeSequence]);

  useEffect(() => {
    if (commercialSpaceOptions.length === 0) return;
    const stillValid = commercialSpaceOptions.some(
      (o) => o.id === selectedCommercialSpace,
    );
    if (!stillValid) setSelectedCommercialSpace(commercialSpaceOptions[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercialSpaceOptions]);

  // Keep the size selection on a real option from the active list.
  // Swap to first option when the size flavour flips (sqft ↔ bedroom)
  // because the customer toggled between an Airbnb space and a
  // regular one. Also clears the override so the recommended hours
  // for the new bracket take effect cleanly.
  useEffect(() => {
    if (commercialSizeOptions.length === 0) return;
    const stillValid = commercialSizeOptions.some(
      (o) => o.id === selectedCommercialSize,
    );
    if (!stillValid) {
      setSelectedCommercialSize(commercialSizeOptions[0].id);
      setCommercialHoursOverride(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercialSizeOptions]);

  // hasPets is a Yes/No flag from `flow_config.questions`. If admin
  // has configured a default we honour it; otherwise default to 'no'
  // (less surprising than presuming pets).
  useEffect(() => {
    if (!hasPets) setHasPets('no');
  }, []);

  // Pre-pick the first weekday so the recurring weekday step (step 7) isn't
  // empty when entered. ONLY for recurring bookings — a one-off has no
  // weekday step and must not be weekday-restricted, otherwise the date
  // calendar (which greys out any day != selectedDay) would offer only the
  // defaulted weekday (e.g. every Monday). Leave selectedDay empty for
  // one-off so every available date is selectable.
  useEffect(() => {
    // Only weekday cadences pre-pick a day — daily has no weekday step and
    // must not weekday-restrict the start calendar.
    if (isWeekdayFreq(frequency) && !selectedDay && weekdayOptions.length > 0) {
      setSelectedDay(weekdayOptions[0].id);
    }
  }, [weekdayOptions, frequency]);

  const addonsNote = useMemo(() => {
    return (
      flowCategory?.flow_config?.addons_note ??
      'Some of these additional services may need to be booked on a separate day with a specialist professional.'
    );
  }, [flowCategory]);

  // Title/subtitle pairs per step. Pulled out of the giant JSX
  // ternary that used to live inline so a new category can override
  // the wording later via flow_config without surgery on JSX.
  const stepCopy = useMemo<Record<number, { title: string; sub?: string }>>(() => {
    const catName = flowCategory?.name?.toLowerCase() ?? 'service';
    // Recurring bookings (incl. daily) pick a START date (the series repeats
    // from it), so the calendar step (8) reads "Please choose a start date".
    // One-off bookings keep "Select a date".
    const isRecurring = isRecurringFreq(frequency);
    return {
      1: { title: `Choose your ${catName} session`, sub: 'Select the service that best suits your needs' },
      2: {
        title: isCommercialService
          ? 'Which type of space do you need this service for?'
          : 'How large is your space?',
        sub: 'Select the size of your space',
      },
      3: { title: 'How many hours do you need?', sub: 'Select the duration of the service' },
      4: { title: 'Do you have pets?' },
      5: { title: 'Your expectations' },
      6: { title: `How often do you need ${catName}?` },
      7: { title: 'Which day works best?' },
      8: { title: isRecurring ? 'Please choose a start date' : 'Select a date' },
      9: { title: 'Choose your time' },
      10: { title: 'Any add-ons?' },
      11: { title: `This is the Cherry Picked professional\nwe recommend for your job` },
      12: {
        title: 'Prefer a backup pro?',
        sub: 'If your chosen pro becomes unavailable, we can assign an alternative — or you can choose your own backups.',
      },
    };
  }, [flowCategory, isCommercialService, frequency]);

  // The smallest bookable duration for this category, in minutes — taken
  // from the duration picker's own options (e.g. childcare's smallest is
  // 2h). Used as a hard floor so a missing / stale selection can never
  // collapse the booking below the category minimum.
  const minDurationMinutes = useMemo(() => {
    const mins = durationOptions
      .map((o) => Math.round(durationIdToHours(o.id) * 60))
      .filter((m) => m > 0);
    return mins.length > 0 ? Math.min(...mins) : 60;
  }, [durationOptions]);

  // Hours the customer asked for (e.g. "3h30" → 210 minutes). The
  // backend uses this as the slot duration so we offer only slots
  // long enough for the requested cleaning. Admin can configure
  // duration ids as "3h" / "3h30" or as plain numbers ("3", "3.5") —
  // both shapes need to map to minutes.
  const desiredDurationMinutes = useMemo(() => {
    // Commercial bookings don't run through the residential
    // "duration" step — instead the workspace-size selection (sq ft
    // or Airbnb bedrooms) feeds a recommended hours value the
    // customer can then override. Convert hours → minutes here so
    // every downstream consumer (slot search, draft persist, basket)
    // sees a single number.
    if (isCommercialService) {
      return Math.round(commercialEffectiveHours * 60);
    }
    // Fall back to the category MINIMUM (not the service's raw
    // duration_minutes, which is 60 for childcare and was the source of
    // the "reverts to 1 hour" bug) and floor the parsed value at it — so
    // a stale '1' restored from the draft, or an empty selection coming
    // back from checkout, can never drop the booking below 2h.
    let parsed = minDurationMinutes;
    if (selectedDuration) {
      const minsOnly = /^(\d+)\s*min$/i.exec(selectedDuration);
      const hAndM = /^(\d+)h(\d+)?$/i.exec(selectedDuration);
      if (minsOnly) {
        parsed = parseInt(minsOnly[1], 10);
      } else if (hAndM) {
        const h = parseInt(hAndM[1], 10);
        const mm = hAndM[2] ? parseInt(hAndM[2], 10) : 0;
        parsed = h * 60 + mm;
      } else {
        const num = parseFloat(selectedDuration);
        if (Number.isFinite(num) && num > 0) parsed = Math.round(num * 60);
      }
    }
    return Math.max(minDurationMinutes, parsed);
  }, [
    isCommercialService,
    commercialEffectiveHours,
    selectedDuration,
    minDurationMinutes,
  ]);

  // Fetch availability when we cross into the calendar / time-picker
  // steps, or when the calendar month moves, or when the duration /
  // service changes. A 30-day window from the calendar viewport
  // start lets the customer see "next month also has openings"
  // without a second call.
  useEffect(() => {
    if (sessionStep < 8) return;
    if (!resolvedService) return;
    const from = new Date(calendarMonth.getTime());
    from.setDate(1);
    const to = new Date(from.getTime());
    to.setDate(to.getDate() + 41); // ~6 weeks — full calendar tile
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    const draft = getBookingDraft();
    const lat = draft?.address?.latitude ?? null;
    const lng = draft?.address?.longitude ?? null;
    const city = draft?.address?.city ?? null;
    listAvailablePros({
      service_id: resolvedService.id,
      date_from: ymd(from),
      date_to: ymd(to),
      duration_minutes: desiredDurationMinutes,
      lat,
      lng,
      city,
    })
      .then((res) => setAvailablePros(res.data))
      .catch((err: any) => {
        setAvailabilityError(
          err?.response?.data?.message ?? err?.message ?? 'Failed to load',
        );
      })
      .finally(() => setAvailabilityLoading(false));
  }, [sessionStep, resolvedService, calendarMonth, desiredDurationMinutes]);

  // Union of every day at least one pro has at least one slot.
  // Drives the calendar's "selectable" highlight on step 8.
  const availableDays = useMemo(() => {
    const out = new Set<string>();
    for (const pro of availablePros) {
      for (const [day, slots] of Object.entries(pro.slots_by_date)) {
        if (slots.length > 0) out.add(day);
      }
    }
    return out;
  }, [availablePros]);

  // Union of every slot-start across pros for the picked date.
  // Sorted ascending so the grid reads top-left → bottom-right.
  // For booking shapes that don't gate by live availability
  // (recurring + commercial), we fall back to a default 09:00 → 17:00
  // hourly grid when no live slot is returned — the pro is assigned at
  // checkout, so we don't want step 9 to dead-end on "no times".
  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [] as string[];
    const key = ymd(selectedDate);
    const set = new Set<string>();
    for (const pro of availablePros) {
      for (const slot of pro.slots_by_date[key] ?? []) {
        set.add(slot.start);
      }
    }
    // For non-commercial shapes, fall back to a default 09:00 → 17:00
    // hourly grid when the live map returned nothing, so step 9 isn't a
    // dead-end (the backend matches a pro at checkout).
    //
    // COMMERCIAL is deliberately EXCLUDED: it now gates by live
    // availability like residential. The old fake grid let the customer
    // pick a time no pro was free at, then the pick-pro step (step 11)
    // dead-ended on "no pros are free at your chosen slot". Showing only
    // real, bookable times keeps the two steps consistent.
    if (set.size === 0 && !isCommercialService) {
      for (let h = 9; h <= 17; h++) {
        set.add(`${String(h).padStart(2, '0')}:00`);
      }
    }
    let times = Array.from(set).sort();
    // Same-day lead-time buffer: on today's date only offer start times at
    // least SAME_DAY_LEAD_MIN ahead of now. Belt-and-braces alongside the
    // backend filter — and the ONLY guard for the local fallback grid above.
    // Anchor BOTH "today" and "now" to UK time: the slot grid is UK
    // wall-clock, so a browser-local `new Date()` cutoff shifted this by the
    // browser↔UK offset and, on a browser ahead of the UK (e.g. IST), hid
    // every same-day slot even when the UK still had hours of availability.
    const uk = ukNow();
    if (key === uk.ymd) {
      const minMinutes = uk.minutesOfDay + SAME_DAY_LEAD_MIN;
      times = times.filter((hhmm) => {
        const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10) || 0);
        return h * 60 + m >= minMinutes;
      });
    }
    return times;
  }, [availablePros, selectedDate, isCommercialService]);

  // Pros who can actually cover the picked date+time, sorted by
  // rating + featured flag. Drives step 11's "recommended" card +
  // grid. When no time slot is picked yet (defensive — step 9 ought
  // to enforce one), fall back to any pro available on that date.
  const slotMatchingPros = useMemo(() => {
    if (!selectedDate) return [] as AvailablePro[];
    const dateKey = ymd(selectedDate);
    const filtered = availablePros.filter((p) => {
      const slots = p.slots_by_date[dateKey] ?? [];
      if (slots.length === 0) return false;
      if (!selectedTimeSlot) return true;
      return slots.some((s) => s.start === selectedTimeSlot);
    });
    return filtered.slice().sort((a, b) => {
      // Featured first, then by rating, then by review count — same
      // ordering the SelectProfessional grid uses, so a customer
      // jumping between the two screens sees the same headliner.
      if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
      if (a.rating_avg !== b.rating_avg) return b.rating_avg - a.rating_avg;
      return b.rating_count - a.rating_count;
    });
  }, [availablePros, selectedDate, selectedTimeSlot]);

  // Step 11 used to auto-pick the top pro so Next was live the moment
  // the page rendered — but that made it possible to advance without
  // any deliberate click, which surprised customers (the screenshot
  // bug report on 2026-06-03). The picker is now strictly opt-in:
  // either tap a card or hit "Book with X" on the featured tile.

  const featuredPro = slotMatchingPros[0] ?? null;
  const otherPros = slotMatchingPros.slice(1);

  // When the picked date+time has no eligible pro, fetch the 3 pros
  // with the soonest alternative slot so the customer can switch to
  // one of those instead of getting stuck on the empty state. Mirrors
  // the existing SelectProfessionalPage fallback behaviour.
  const [nearestAlternatives, setNearestAlternatives] = useState<
    Array<AvailablePro & { next_slot_at: string | null }>
  >([]);
  const [nearestLoading, setNearestLoading] = useState(false);
  useEffect(() => {
    if (sessionStep !== 11) return;
    if (availabilityLoading) return;
    if (featuredPro) {
      setNearestAlternatives([]);
      return;
    }
    if (!resolvedService || !selectedDate) return;
    const draft = getBookingDraft();
    const lat = draft?.address?.latitude ?? null;
    const lng = draft?.address?.longitude ?? null;
    const city = draft?.address?.city ?? null;
    let cancelled = false;
    setNearestLoading(true);
    listNearestAvailablePros({
      service_id: resolvedService.id,
      target_date: ymd(selectedDate),
      duration_minutes: desiredDurationMinutes,
      lat,
      lng,
      city,
      days_ahead: 30,
      limit: 3,
    })
      .then((res) => {
        if (cancelled) return;
        setNearestAlternatives(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setNearestAlternatives([]);
      })
      .finally(() => {
        if (!cancelled) setNearestLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    sessionStep,
    availabilityLoading,
    featuredPro,
    resolvedService,
    selectedDate,
    desiredDurationMinutes,
  ]);

  // Switch the wizard onto an alternative pro + their suggested slot.
  // Re-walks the customer back to step 8/9 so they can confirm — the
  // calendar repaints with the picked date, the time grid jumps to
  // the proposed slot, and the basket recomputes against the new
  // date/time before they pay.
  const pickAlternative = (
    alt: AvailablePro & { next_slot_at: string | null },
  ) => {
    setSelectedProId(alt.id);
    if (alt.next_slot_at) {
      const dt = new Date(alt.next_slot_at);
      if (!Number.isNaN(dt.getTime())) {
        setSelectedDate(dt);
        const hh = String(dt.getHours()).padStart(2, '0');
        const mm = String(dt.getMinutes()).padStart(2, '0');
        setSelectedTimeSlot(`${hh}:${mm}`);
      }
    }
    setSessionStep(12);
  };
  const proDisplayName = (p: AvailablePro): string =>
    [p.user?.first_name, p.user?.last_name].filter(Boolean).join(' ') ||
    p.business_name ||
    '—';

  const currentMainStep = sessionStep >= 11 ? 5 : (sessionStep >= 10 ? 4 : (sessionStep >= 6 ? 3 : 2));

  // The first step the wizard can show — step 1 normally, or the first
  // real step when the service picker is hidden (single-service category
  // like Childcare). Back from here exits the wizard instead of walking
  // to a step that isn't in the active order.
  const firstWizardStep = stepOrder[0] ?? 1;

  // Per-step "Next button enable" gate — mirrors what each step
  // physically asks the customer to pick. Anything left null / empty
  // means the customer hasn't answered yet → button stays disabled.
  // Commercial bookings skip steps 3-5, so those branches are gated
  // on `isCommercialService` to avoid blocking the wizard on fields
  // that aren't rendered.
  const canProceedStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!selectedServiceId;
      case 2:
        if (isCommercialService) {
          if (!selectedCommercialSpace) return false;
          if (
            selectedCommercialSpace === 'other' &&
            !otherSpaceDetails.trim()
          ) {
            return false;
          }
          // Workspace-size selection is also required on the
          // commercial flow so the booking has a duration. The
          // override hours field is optional.
          if (!selectedCommercialSize) return false;
          return true;
        }
        return !!selectedSpace;
      case 3:
        // Residential-only — duration. Commercial skips this step.
        if (isCommercialService) return true;
        return !!selectedDuration;
      case 4:
        if (isCommercialService) return true;
        return !!hasPets;
      case 5:
        // "Anything we should know?" — free-form notes, optional.
        return true;
      case 6:
        return !!frequency;
      case 7:
        return !!selectedDay;
      case 8:
        return !!selectedDate;
      case 9:
        return !!selectedTimeSlot;
      case 10:
        // Add-ons are optional — Next is always live on this step.
        return true;
      case 11:
        // Customer must explicitly pick a pro — tapping any card sets
        // selectedProId. Auto-picking the recommendation was confusing
        // (Next looked live without a click). Also gate on the
        // recommendation having loaded so the customer doesn't advance
        // on a stale picture of who's available.
        return !!selectedProId && !!featuredPro && !availabilityLoading;
      case 12:
        return !!substitutePreference;
      default:
        return true;
    }
  };

  const steps = [
    { id: 1, label: 'Location', active: true },
    { id: 2, label: 'Session', active: currentMainStep >= 2 },
    { id: 3, label: 'Schedule', active: currentMainStep >= 3 },
    { id: 4, label: 'Add ons', active: currentMainStep >= 4 },
    { id: 5, label: 'Confirm', active: currentMainStep >= 5 }
  ];

  const handleNext = () => {
    // Steps 1–10 are a linear walk through the active stepOrder, which
    // already encodes the commercial 2→6 jump and childcare's slimmer
    // sequence. Steps 11 (pick pro) and 12 (backup pro) keep their
    // bespoke routing/auth logic below.
    if (sessionStep !== 11 && sessionStep !== 12) {
      const idx = stepOrder.indexOf(sessionStep);
      const next =
        idx >= 0 && idx < stepOrder.length - 1 ? stepOrder[idx + 1] : 11;
      setSessionStep(next);
    } else if (sessionStep === 11) {
      // Both one-off and recurring bookings go through the alternative-pro
      // opt-in (step 12): the customer says whether Cherry Picked may
      // assign an alternative if their pick is unavailable, or chooses
      // their own backups. (One-off used to skip step 12 straight to
      // checkout, which removed the opt-in + "browse & tick alternatives".)
      setSessionStep(12);
    } else if (sessionStep === 12) {
      persistDraft();
      // When the customer wants to pick a backup cleaner, route them
      // to the secondary-preferred picker first regardless of auth.
      // The secondary-preferred page's Save handler then forwards
      // authed customers to /your-appointment or /continue-booking.
      if (substitutePreference === 'no') {
        navigate('/secondary-preferred');
      } else if (isCustomerAuthed) {
        // No backup wanted + already signed in → straight to checkout
        // (one-off) or recurring confirm (subscription).
        const isSubscription = isRecurringFreq(frequency);
        navigate(isSubscription ? '/continue-booking' : '/your-appointment');
      } else {
        // No backup wanted + guest → sign-in detour. Existing
        // customers sign in; new ones use the "Create account" link.
        // Resume target depends on the booking mode they picked.
        const isSubscription = isRecurringFreq(frequency);
        navigate('/signin/customer', {
          state: {
            from: isSubscription ? '/continue-booking' : '/your-appointment',
          },
        });
      }
    } else {
      persistDraft();
      if (isCustomerAuthed) {
        navigate('/your-appointment');
      } else {
        navigate('/signin/customer', {
          state: { from: '/your-appointment' },
        });
      }
    }
  };

  // Snapshot the wizard state into the localStorage draft so the
  // /continue-booking page (post-signup) can resume the basket. The
  // service / category ids are resolved at checkout-time from the
  // public catalog; we pass the selected code via `meta.service_code`
  // as a loose hint.
  function persistDraft() {
    const isSubscription = isRecurringFreq(frequency);
    const frequencyMap: Record<
      string,
      'DAILY' | 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY'
    > = {
      daily: 'DAILY',
      weekly: 'WEEKLY',
      biweekly: 'FORTNIGHTLY',
      monthly: 'MONTHLY',
    };
    const dayMap: Record<string, number> = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    // Derive billable hours from the picked duration id. Use the
    // already-resolved minutes so "4h30" maps to 4.5 (not 430).
    //
    // Keep the value fractional — the backend's priceItems treats qty
    // as `Number(qty)` and only rejects qty<=0, so half-hours work
    // server-side. Math.round'ing to whole hours here used to silently
    // overcharge every 30-min increment (basket headline "£21/h ×
    // 2h30" but line_total = 21 × Math.round(2.5) = 21 × 3 = £63
    // instead of £52.50 — and 30-min services were billed double).
    // Floor at 1 minute (1/60h) so qty > 0 stays true.
    const hours = Math.max(
      1 / 60,
      Math.round((desiredDurationMinutes / 60) * 100) / 100,
    );
    const slot = selectedTimeSlot ?? '11:00';
    const startTime = `${slot}:00`;

    // Build the real items[] payload the backend expects. One line
    // for the base service (PER_HOUR → quantity = hours, otherwise
    // 1), plus one line per chosen add-on. Add-on quantity is the
    // block count: 30min = 1 block, 1hour = 2 blocks (per the
    // seeded block_minutes = 30).
    const items: Array<{
      service_id?: string | null;
      service_addon_id?: string | null;
      quantity: number;
      properties?: Record<string, any> | null;
    }> = [];
    if (resolvedService) {
      items.push({
        service_id: resolvedService.id,
        quantity:
          resolvedService.pricing_unit === 'PER_HOUR' ? hours : 1,
        properties: {
          space: selectedSpace,
          has_pets: hasPets,
          commercial_space: isCommercialService
            ? selectedCommercialSpace
            : null,
          // Workspace size + override hours snapshot — surfaces the
          // customer's choice to the pro and lets us audit later
          // whether the recommended hours matched reality.
          commercial_size: isCommercialService
            ? selectedCommercialSize
            : null,
          commercial_size_kind: isCommercialService
            ? isAirbnbCommercialSpace
              ? 'AIRBNB_BEDROOMS'
              : 'SQ_FT'
            : null,
        },
      });
    }
    for (const sel of selectedAddons) {
      const addon = addons.find((a) => a.id === sel.id);
      if (!addon) continue;
      const blocks = sel.duration === '1hour'
        ? Math.max(1, Math.round(60 / addon.block_minutes))
        : 1;
      items.push({
        service_addon_id: addon.id,
        quantity: blocks,
        properties: { duration: sel.duration },
      });
    }

    setBookingDraft({
      mode: isSubscription ? 'SUBSCRIPTION' : 'ONE_OFF',
      frequency: isSubscription ? frequencyMap[frequency] : undefined,
      day_of_week: isSubscription ? dayMap[selectedDay] : undefined,
      start_time: isSubscription ? startTime : undefined,
      // Capture the customer's IANA timezone alongside the wall-clock
      // pick. The backend's spawner uses it to compute scheduled_start
      // in this zone — without it, "Sunday 09:00" gets persisted in
      // UTC and slides by the offset on every spawned cycle (audit
      // 2026-06-04, high — UTC drift fix).
      timezone: isSubscription
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined,
      scheduled_start: isSubscription ? undefined : computeScheduledStart(),
      duration_minutes: desiredDurationMinutes,
      service_id: resolvedService?.id ?? null,
      service_category_id: resolvedService?.category.id ?? null,
      professional_id: selectedProId,
      address: getBookingDraft()?.address ?? null,
      items,
      customer_note: notes || null,
      customer_note_images: expectationImages.length ? expectationImages : null,
      meta: {
        // Carry over fields set OUTSIDE the wizard (coupon code, voucher,
        // substitute preference, nominated backup pros). setBookingDraft
        // replaces the whole row, so without this spread those were wiped
        // on navigate. The explicit keys below override with current
        // wizard state.
        ...(getBookingDraft()?.meta ?? {}),
        service_code: resolvedService?.code ?? null,
        space: selectedSpace,
        has_pets: hasPets,
        hours,
        // Persist the chosen duration-option id (e.g. "3h30") too — not
        // just the numeric `hours`. On back-navigation / remount the
        // wizard reads this; omitting it reset `durationTouched` and
        // reverted the customer's pick to the property-size
        // recommendation (e.g. 3.5h → 2h).
        duration_id: selectedDuration || null,
        frequency,
        day: selectedDay,
        date: selectedDate ? ymd(selectedDate) : null,
        times: selectedTimeSlot ? [selectedTimeSlot] : [],
        addons: selectedAddons,
        commercial_space: isCommercialService ? selectedCommercialSpace : null,
        commercial_space_other:
          isCommercialService && selectedCommercialSpace === 'other'
            ? otherSpaceDetails
            : null,
        commercial_size: isCommercialService ? selectedCommercialSize : null,
        commercial_size_kind: isCommercialService
          ? isAirbnbCommercialSpace
            ? 'AIRBNB_BEDROOMS'
            : 'SQ_FT'
          : null,
        commercial_hours_override:
          isCommercialService && commercialHoursOverride !== null
            ? commercialHoursOverride
            : null,
        // Preserve the wizard's current step so Back from any
        // downstream page (/secondary-preferred, /your-appointment,
        // /continue-booking) resumes the customer at the step they
        // left — not at step 1. setBookingDraft replaces the whole
        // row, so we re-stamp this here too.
        last_session_step: sessionStep,
        // Mark this booking as having come through the SESSION wizard so
        // the checkout Back button returns here (not the treatment flow),
        // even if the catalog hasn't loaded to resolve the category flow.
        flow_entry: 'SESSION',
      },
    });
  }

  function computeScheduledStart(): string {
    // Falls back to "today + 7 days at 11:00" if the customer
    // somehow skipped the calendar — defensive only; step 8 ought
    // to require a pick before Next.
    const d = selectedDate ? new Date(selectedDate.getTime()) : new Date();
    if (!selectedDate) d.setDate(d.getDate() + 7);
    // Interpret the picked slot as UK (Europe/London) wall-clock regardless of
    // the customer's browser timezone. The old browser-local `setHours` shifted
    // bookings made from a non-UK browser, so the checkout's slot re-validation
    // rejected them ("professional no longer available" → "Could not start
    // checkout"). For a UK browser this is identical to the old behaviour.
    return ukSlotToISO(d, selectedTimeSlot ?? '11:00');
  }

  // Basket-friendly shape for the sidebar component (price, name).
  // Mirrors the old hardcoded `selectedServiceData` minus the
  // `details` array — those bullets are admin-managed `highlights`
  // on the seeded service rows and live next to step 1 instead.
  const selectedServiceData = resolvedService
    ? {
        id: resolvedService.id,
        name: resolvedService.name,
        price: Number(resolvedService.base_price),
      }
    : null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 md:px-8 pt-6">
        {sessionStep === firstWizardStep ? (
          // First wizard step → back to the address step (the page that
          // actually precedes /booking), not the home page. BookingLocation
          // only pre-fills the saved address; it never auto-forwards, so
          // there's no bounce-back loop.
          <Link to="/booking-location">
            <SecondaryButton icon={<span className="material-icons text-[18px]">navigate_before</span>}>
              Back
            </SecondaryButton>
          </Link>
        ) : (
          <SecondaryButton
            onClick={() => {
              // Walk the active stepOrder backwards so Back mirrors the
              // forward path (handles the commercial 2→6 jump and
              // childcare's slimmer order). Step 12 (backup pro) isn't in
              // stepOrder, so fall back to the previous number for it.
              const idx = stepOrder.indexOf(sessionStep);
              if (idx > 0) {
                setSessionStep(stepOrder[idx - 1]);
              } else {
                setSessionStep(prev => Math.max(1, prev - 1));
              }
            }}
            icon={<span className="material-icons text-[18px]">navigate_before</span>}
          >
            Back
          </SecondaryButton>
        )}
      </div>

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 py-8 md:py-12 pb-32 md:pb-12 w-full">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <div className="text-center mb-12">
              <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2 whitespace-pre-line">
                {stepCopy[sessionStep]?.title ?? ''}
              </h1>
              <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
                {stepCopy[sessionStep]?.sub ?? 'We are almost there'}
              </p>
            </div>

            {sessionStep !== 11 && (
              <div className="flex items-center justify-center mb-12 w-full px-2">
                {steps.map((step, index) => (
                  <div key={step.id} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-[24px] h-[24px] rounded-full flex items-center justify-center mb-2 ${step.active
                        ? 'bg-[#8e2a2f]'
                        : 'bg-[#edf2f0]'
                        }`}>
                        <div className={`rounded-full ${step.active
                          ? 'w-[8px] h-[8px] bg-white'
                          : 'w-[6px] h-[6px] bg-[#c6d8d3]'
                          }`} />
                      </div>
                      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] md:text-sm leading-[18px] md:leading-[21px] tracking-[-0.1px] whitespace-nowrap font-bold">
                        {step.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="w-4 sm:w-12 md:w-20 h-[2px] bg-[#edf2f0] mb-[24px] md:mb-[29px] mx-1 shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Content Switcher */}
            {sessionStep === 1 && (
              <div className="max-w-[570px] mx-auto w-full space-y-6">
                {flowServices.length === 0 ? (
                  <p className="text-center text-sm text-[#908e8e]">
                    {catalogReady
                      ? `No ${flowCategory?.name?.toLowerCase() ?? 'service'} options available right now.`
                      : 'Loading services…'}
                  </p>
                ) : (
                  flowServices.map((service) => {
                    const isSelected = selectedServiceId === service.id;
                    return (
                      <button
                        key={service.id}
                        onClick={() => setSelectedServiceId(service.id)}
                        className={`w-full text-left border rounded-[10px] px-4 transition-all duration-300 cursor-pointer block relative ${isSelected
                          ? 'border-[#8e2a2f] bg-white py-4'
                          : 'border-[#eee] bg-white hover:border-[#8e2a2f] h-[80px]'
                          }`}
                      >
                        <div className="flex flex-col justify-center h-full">
                          {service.badge && (
                            <div className="mb-1">
                              <span className="font-['DM_Sans',sans-serif] font-bold text-[#8e2a2f] text-[10px] uppercase tracking-[1px]">
                                {service.badge}
                              </span>
                            </div>
                          )}

                          <div className={`flex items-start justify-between w-full ${isSelected ? 'mb-2' : ''} transition-all`}>
                            <h3 className={`font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px]`}>
                              {service.name}
                            </h3>
                            <p className={`font-['DM_Sans',sans-serif] text-[16px] leading-[24px]`}>
                              {/* Inclusive "from" price (cheapest pro's
                                  rate + platform fee); rates vary by pro. */}
                              <span className="text-[#908e8e] text-[13px]">from </span>
                              <span className="text-[#8e2a2f]">£{Number(service.from_price ?? service.base_price).toFixed(2)}</span>
                              <span className="text-[#908e8e]">{service.pricing_unit === 'PER_HOUR' ? '/hr' : ''}</span>
                            </p>
                          </div>

                          {service.highlights && service.highlights.length > 0 && isSelected && (
                            <ul className="space-y-0 mt-2">
                              {service.highlights.map((detail, idx) => (
                                <li key={idx} className="flex items-center gap-3">
                                  <div className="w-1 h-1 rounded-full bg-[#908e8e]" />
                                  <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[16px] leading-[24px]">
                                    {detail}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            )}
            {sessionStep === 2 && (
              <div className="max-w-[800px] mx-auto w-full">
                {isCommercialService ? (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {commercialSpaceOptions.map((space) => (
                        <div key={space.id} className="w-full">
                          <button
                            onClick={() => setSelectedCommercialSpace(space.id)}
                            className={`w-full h-[60px] text-left border rounded-lg px-5 transition-all cursor-pointer flex items-center ${selectedCommercialSpace === space.id
                              ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                              : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                              }`}
                          >
                            <h3 className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${selectedCommercialSpace === space.id ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
                              }`}>
                              {space.label}
                            </h3>
                          </button>
                          {space.id === 'other' && selectedCommercialSpace === 'other' && (
                            <div className="mt-3">
                              <input
                                type="text"
                                value={otherSpaceDetails}
                                onChange={(e) => setOtherSpaceDetails(e.target.value)}
                                placeholder="Please specify your type of space"
                                className="w-full h-[50px] border border-[#8e2a2f] rounded-lg px-5 outline-none font-['DM_Sans',sans-serif] text-[14px]"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Workspace size + recommended hours. Bedroom
                        count for Airbnb / holiday rentals, sq ft for
                        everything else. The chosen bracket's
                        recommended_hours pre-fills the booking
                        duration; the override input below lets the
                        customer tweak. */}
                    {selectedCommercialSpace ? (
                      <div className="border-t border-[#ede8e9] pt-6">
                        <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#3a3335] text-[18px] leading-[27px] mb-1">
                          {isAirbnbCommercialSpace
                            ? 'How many bedrooms?'
                            : 'How large is the workspace?'}
                        </h2>
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mb-4">
                          We'll suggest a number of cleaning hours based on your answer. You can adjust the hours below.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {commercialSizeOptions.map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                setSelectedCommercialSize(opt.id);
                                setCommercialHoursOverride(null);
                              }}
                              className={`text-left border rounded-lg p-4 transition-all cursor-pointer ${selectedCommercialSize === opt.id
                                ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                                : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                                }`}
                            >
                              <div className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${selectedCommercialSize === opt.id ? 'text-[#8e2a2f]' : 'text-[#3a3335]'}`}>
                                {opt.label}
                              </div>
                              <div className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] mt-1">
                                Recommended {opt.recommended_hours}h
                              </div>
                            </button>
                          ))}
                        </div>

                        <div className="mt-5 flex items-center gap-3">
                          <label className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[13px] whitespace-nowrap">
                            Or enter hours manually:
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={24}
                            step={0.5}
                            value={
                              commercialHoursOverride === null
                                ? ''
                                : String(commercialHoursOverride)
                            }
                            onChange={(e) => {
                              const raw = e.target.value.trim();
                              if (raw === '') {
                                setCommercialHoursOverride(null);
                                return;
                              }
                              const n = Number(raw);
                              if (Number.isFinite(n) && n > 0) {
                                setCommercialHoursOverride(n);
                              }
                            }}
                            placeholder={`${commercialSizeOptions.find((o) => o.id === selectedCommercialSize)?.recommended_hours ?? ''}`}
                            className="w-[100px] h-[40px] border border-[#ede8e9] rounded-md px-3 text-[14px] font-['DM_Sans',sans-serif] outline-none focus:border-[#8e2a2f]"
                          />
                          <span className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px]">
                            (we'll bill {commercialEffectiveHours}h)
                          </span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="max-w-[420px] mx-auto space-y-4">
                    {spaceSizeOptions.map((space) => (
                      <button
                        key={space.id}
                        onClick={() => {
                          // A genuine user size change re-arms the
                          // recommended-hours default. Re-tapping the same
                          // size — or a programmatic restore on back-nav /
                          // remount — leaves a hand-picked duration intact.
                          // (This is why the reset lives here, not in a
                          // useEffect on selectedSpace which also fired on
                          // restores and reverted the hours to default.)
                          if (space.id !== selectedSpace) {
                            setDurationTouched(false);
                          }
                          setSelectedSpace(space.id);
                        }}
                        className={`w-full h-[50px] text-left border rounded-lg px-5 transition-all cursor-pointer flex items-center ${selectedSpace === space.id
                          ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                          : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                          }`}
                      >
                        <h3 className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${selectedSpace === space.id ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
                          }`}>
                          {space.label}
                        </h3>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {sessionStep === 3 && (
              <div className="max-w-[420px] mx-auto w-full">
                {/* Recommended-hours hint driven by the property size the
                    customer picked on step 2. The matching duration is
                    pre-selected below (and ringed when they tap away from
                    it) so the recommendation is always visible. */}
                {!isCommercialService && recommendedResidentialHours != null && (
                  <p className="text-center font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] leading-[20px] mb-4">
                    Based on your space
                    {selectedSpaceLabel ? ` (${selectedSpaceLabel})` : ''}, we
                    recommend{' '}
                    <span className="font-bold text-[#8e2a2f]">
                      {recommendedResidentialHours}h
                    </span>
                    . Adjust below if you need more or less.
                  </p>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 md:gap-4">
                  {durationOptions.map((opt) => {
                    const isSelected = selectedDuration === opt.id;
                    const isRecommended = opt.id === recommendedDurationId;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => {
                          setSelectedDuration(opt.id);
                          setDurationTouched(true);
                        }}
                        className={`w-full h-[50px] text-center border rounded-lg transition-all cursor-pointer flex items-center justify-center ${isSelected
                          ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                          : isRecommended
                            ? 'border-[#8e2a2f]/40 bg-white hover:border-[#8e2a2f]'
                            : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                          }`}
                      >
                        <h3 className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${isSelected ? 'text-[#8e2a2f]' : 'text-[#908e8e]'
                          }`}>
                          {opt.label}
                        </h3>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {sessionStep === 4 && (
              <div className="max-w-[420px] mx-auto w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { id: 'yes', label: 'Yes' },
                  { id: 'no', label: 'No' },
                ].map((petOption) => (
                  <button
                    key={petOption.id}
                    onClick={() => setHasPets(petOption.id)}
                    className={`w-full h-[50px] text-left border rounded-lg px-5 transition-all cursor-pointer flex items-center ${hasPets === petOption.id
                      ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                      : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                      }`}
                  >
                    <h3 className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${hasPets === petOption.id ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
                      }`}>
                      {petOption.label}
                    </h3>
                  </button>
                ))}
              </div>
            )}
            {sessionStep === 5 && (
              <div className="max-w-[420px] mx-auto w-full">
                <div className="border border-[#eee] rounded-lg p-5 bg-white focus-within:border-[#8e2a2f] focus-within:shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] transition-all">
                  <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-base leading-[24px] mb-2">
                    {textQuestionLabel}
                  </h3>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Write something..."
                    className="w-full h-[120px] resize-none outline-none font-['DM_Sans',sans-serif] text-sm leading-[21px] text-[#3a3335] placeholder:text-[#908e8e]"
                    style={{ fontVariationSettings: "'opsz' 14" }}
                  />
                </div>
                <ExpectationPhotos
                  value={expectationImages}
                  onChange={setExpectationImages}
                />
              </div>
            )}
            {sessionStep === 6 && (
              <div className="max-w-[420px] mx-auto w-full space-y-4">
                {frequencyOptions.map((freq) => (
                  <button
                    key={freq.id}
                    onClick={() => setFrequency(freq.id)}
                    className={`w-full h-[50px] text-left border rounded-lg px-5 transition-all cursor-pointer flex items-center ${frequency === freq.id
                      ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                      : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                      }`}
                  >
                    <h3 className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${frequency === freq.id ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
                      }`}>
                      {freq.label}
                    </h3>
                  </button>
                ))}
              </div>
            )}
            {sessionStep === 7 && (
              <div className="max-w-[420px] mx-auto w-full grid grid-cols-2 gap-4">
                {weekdayOptions.map((day) => (
                  <button
                    key={day.id}
                    onClick={() => setSelectedDay(day.id)}
                    className={`w-full h-[50px] text-left border rounded-lg px-5 transition-all cursor-pointer flex items-center ${selectedDay === day.id
                      ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] bg-white'
                      : 'border-[#eee] bg-white hover:border-[#8e2a2f]'
                      }`}
                  >
                    <h3 className={`font-['DM_Sans',sans-serif] font-bold text-[14px] ${selectedDay === day.id ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
                      }`}>
                      {day.label}
                    </h3>
                  </button>
                ))}
              </div>
            )}
            {sessionStep === 8 && (
              <AvailabilityCalendar
                month={calendarMonth}
                onChangeMonth={setCalendarMonth}
                availableDays={availableDays}
                selectedDate={selectedDate}
                onSelectDate={(d) => {
                  setSelectedDate(d);
                  setSelectedTimeSlot(null); // reset slot on date change
                }}
                loading={availabilityLoading}
                error={availabilityError}
                // ONLY recurring bookings have a chosen weekday (step 7) —
                // honour it so the start date lands on that day. A one-off
                // has no weekday step, so it must NOT be restricted (else
                // the calendar greys out every day but one). Empty string =
                // "no restriction".
                restrictWeekday={
                  isWeekdayFreq(frequency) ? selectedDay || '' : ''
                }
                // Every booking shape (standard, deep, end-of-tenancy,
                // commercial, recurring) lets the customer pick any
                // future date — we match a pro at checkout instead of
                // gating by the live availability map. Pros rarely have
                // weekend slots pre-populated, so Sundays were
                // otherwise impossible to book across every category.
                allowAnyDate={true}
              />
            )}
            {sessionStep === 9 && (
              <div className="max-w-[420px] mx-auto w-full">
                {!selectedDate ? (
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-center text-sm leading-[21px] tracking-[-0.1px] mb-8">
                    Pick a date first.
                  </p>
                ) : availabilityLoading ? (
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-center text-sm">
                    Loading times…
                  </p>
                ) : slotsForSelectedDate.length === 0 ? (
                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-center text-sm leading-[21px] tracking-[-0.1px] mb-8">
                    {ymd(selectedDate) === ymd(new Date())
                      ? // Today is selected but empty — almost always the
                        // same-day cutoff (bookings need ~2h notice and the
                        // job must finish within the pro's working day), not a
                        // genuine "no availability". Say so instead of the
                        // generic message, which read like a fault.
                        "No more same-day slots for today — same-day bookings need about 2 hours' notice. Please try another date."
                      : 'No times available on this day. Try a different date.'}
                  </p>
                ) : (
                  <>
                    <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-center text-sm leading-[21px] tracking-[-0.1px] -mt-6 mb-8" style={{ fontVariationSettings: "'opsz' 14" }}>
                      Pick a start time. Slot is{' '}
                      <span className="text-[#8e2a2f] font-bold">
                        {selectedDuration}
                      </span>{' '}
                      hours long.
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-8">
                      {slotsForSelectedDate.map((slot) => {
                        const picked = selectedTimeSlot === slot;
                        return (
                          <button
                            key={slot}
                            onClick={() => setSelectedTimeSlot(slot)}
                            className={`py-5 border rounded-lg transition-all font-['DM_Sans',sans-serif] font-bold text-[14px] leading-[24px] ${
                              picked
                                ? 'border-[#8e2a2f] shadow-[0px_4px_15.3px_0px_rgba(134,58,58,0.1)] text-[#8e2a2f] bg-white'
                                : 'border-[#eee] text-[#908e8e] bg-white hover:border-[#8e2a2f]'
                            }`}
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {sessionStep === 10 && (
              <div className="max-w-[900px] mx-auto w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {addons.length === 0 ? (
                    <p className="col-span-2 text-center text-sm text-[#908e8e]">
                      {catalogReady
                        ? 'No add-ons available — skip to next.'
                        : 'Loading add-ons…'}
                    </p>
                  ) : (
                    addons.map((addon) => {
                      const isSelected = selectedAddons.find(a => a.id === addon.id);
                      const currentDuration = isSelected?.duration || '30min';
                      // Seeded add-ons are per-block (£X / block_minutes).
                      // 30min = 1 block, 1hr = 2 blocks → quantity 1 vs 2,
                      // priced as block_price * blocks. Show the inclusive
                      // (display) price so the card matches the all-in basket.
                      const blockPrice = Number(
                        addon.display_price ?? addon.price,
                      );
                      const oneHourBlocks = Math.max(
                        1,
                        Math.round(60 / addon.block_minutes),
                      );
                      const oneHourPrice = blockPrice * oneHourBlocks;

                      return (
                        <div
                          key={addon.id}
                          className={`border rounded-lg p-5 transition-all relative ${isSelected ? 'border-[#8e2a2f] bg-white' : 'border-[#eee] bg-white'
                            }`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-base leading-[24px]">
                                {addon.name}
                              </h3>
                            </div>
                            <button
                              onClick={() => toggleAddon(addon.id)}
                              aria-label={
                                isSelected
                                  ? `Remove ${addon.name}`
                                  : `Add ${addon.name}`
                              }
                              className={`w-6 h-6 rounded shrink-0 flex items-center justify-center border-2 transition-all ${
                                isSelected
                                  ? 'bg-[#8e2a2f] border-[#8e2a2f]'
                                  : 'bg-white border-[#3a3335] hover:border-[#8e2a2f]'
                              }`}
                            >
                              {isSelected && <span className="material-icons text-white text-[16px]">check</span>}
                            </button>
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => isSelected && updateAddonDuration(addon.id, '30min')}
                              className={`flex-1 py-2 px-3 border rounded-lg transition-all font-['DM_Sans',sans-serif] font-bold text-xs ${isSelected && currentDuration === '30min'
                                ? 'bg-[#8e2a2f] border-[#8e2a2f] text-white'
                                : 'bg-white border-[#eee] text-[#908e8e]'
                                }`}
                            >
                              30 min + £{blockPrice.toFixed(2)}
                            </button>
                            <button
                              onClick={() => isSelected && updateAddonDuration(addon.id, '1hour')}
                              className={`flex-1 py-2 px-3 border rounded-lg transition-all font-['DM_Sans',sans-serif] font-bold text-xs ${isSelected && currentDuration === '1hour'
                                ? 'bg-[#8e2a2f] border-[#8e2a2f] text-white'
                                : 'bg-white border-[#eee] text-[#908e8e]'
                                }`}
                            >
                              1 hour + £{oneHourPrice.toFixed(2)}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="bg-[#f2eee2]/50 rounded-lg p-4 mb-8">
                  <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] leading-[18px] text-center">
                    <span className="font-bold">Please note:</span> {addonsNote}
                  </p>
                </div>
              </div>
            )}

            {sessionStep === 11 && (
              <div className="max-w-[900px] mx-auto w-full">
                {availabilityLoading ? (
                  <p className="text-center text-sm text-[#908e8e]">
                    Loading recommended pros…
                  </p>
                ) : !featuredPro ? (
                  <div className="py-8">
                    {nearestAlternatives.length > 0 && (
                      <p className="text-center text-sm text-[#908e8e] mb-6">
                        No pros are free at your chosen slot — here are the
                        three closest alternatives.
                      </p>
                    )}
                    {nearestLoading ? (
                      <p className="text-center text-sm text-[#908e8e]">
                        Finding alternatives…
                      </p>
                    ) : nearestAlternatives.length === 0 ? (
                      // Genuinely no pros for this service nearby in the next
                      // 30 days — warm "coming soon" rather than a bare empty
                      // state.
                      <div className="text-center">
                        <span className="material-icons text-[#8e2a2f] text-[40px] mb-2">
                          storefront
                        </span>
                        <p className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[26px] mb-1">
                          Coming soon to your area
                        </p>
                        <p className="text-sm text-[#908e8e] max-w-[420px] mx-auto">
                          No professionals available in your area just yet —
                          we're adding new pros all the time, so please check
                          back soon!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3 max-w-[420px] mx-auto">
                        {nearestAlternatives.map((alt) => {
                          const slot = alt.next_slot_at
                            ? new Date(alt.next_slot_at)
                            : null;
                          const slotLabel =
                            slot && !Number.isNaN(slot.getTime())
                              ? slot.toLocaleString(undefined, {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })
                              : 'No slot found';
                          return (
                            <button
                              key={alt.id}
                              type="button"
                              onClick={() => pickAlternative(alt)}
                              className="w-full flex items-center gap-3 p-3 rounded-[10px] border border-[#ede8e9] bg-white hover:border-[#8e2a2f] hover:shadow-sm transition-all text-left"
                            >
                              <div className="w-[48px] h-[48px] rounded-full overflow-hidden bg-[#f2eee2] flex items-center justify-center shrink-0">
                                {alt.cover_image_preview_url ?? alt.cover_image_url ? (
                                  <img
                                    src={
                                      alt.cover_image_preview_url ??
                                      alt.cover_image_url ??
                                      ''
                                    }
                                    alt={proDisplayName(alt)}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px]">
                                    {proDisplayName(alt).slice(0, 1).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] truncate">
                                  {proDisplayName(alt)}
                                </p>
                                <p className="font-['DM_Sans',sans-serif] text-[#8e2a2f] text-[12px] font-bold">
                                  Closest: {slotLabel}
                                </p>
                                {alt.rating_count > 0 ? (
                                  <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[11px]">
                                    ★ {Number(alt.rating_avg).toFixed(2)}{' '}
                                    ({alt.rating_count})
                                  </p>
                                ) : null}
                              </div>
                              <span className="material-icons text-[#908e8e] text-[18px]">
                                chevron_right
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Featured Pro Card — top-rated pro who can take
                        the selected date+time slot. */}
                    <div className="flex justify-center mb-16 px-4">
                      <div className="relative w-[250px] bg-[#f7f2e8] rounded-lg p-8 flex flex-col items-center text-center">
                        {featuredPro.is_featured ? (
                          <div className="absolute -top-6 -right-6 w-16 h-16 transform rotate-12 z-10">
                            <svg width="67" height="78" viewBox="0 0 67 78" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M56.5791 17.0694C53.0529 17.16 49.1322 20.7937 46.4818 22.7776C46.1196 23.0482 45.7562 23.3169 45.3921 23.585C43.5957 24.9157 41.8904 26.3526 40.1886 27.8006C39.8162 28.1169 39.4423 28.4313 39.0676 28.7448C37.8452 29.7732 36.6702 30.8457 35.5139 31.9477C35.3806 32.074 35.2474 32.2002 35.1101 32.3303C34.9227 32.5109 34.9227 32.5109 34.7315 32.6952C34.6209 32.8015 34.5102 32.9077 34.3962 33.0172C34.0396 33.4088 33.7706 33.8304 33.485 34.2755C33.3432 34.2081 33.2014 34.1406 33.0554 34.0712C31.7133 33.4333 30.3702 32.7977 29.0261 32.1643C28.3352 31.8386 27.6447 31.5122 26.9551 31.1839C26.2884 30.8666 25.6208 30.5514 24.9524 30.2377C24.6988 30.1181 24.4455 29.9979 24.1925 29.8768C21.7597 28.7137 19.5269 28.0348 16.8458 28.7686C14.4195 29.6669 13.2476 31.3046 12.2066 33.544C12.1002 33.7687 11.9937 33.9934 11.887 34.218C11.5993 34.8254 11.3142 35.4341 11.0298 36.0431C10.7915 36.5528 10.552 37.062 10.3125 37.5712C9.74711 38.7734 9.18404 39.9767 8.62232 41.1806C8.0444 42.4192 7.46236 43.6558 6.8783 44.8914C6.37557 45.9555 5.87558 47.0208 5.37771 48.0871C5.08095 48.7226 4.78317 49.3575 4.48268 49.9913C4.19996 50.5878 3.92072 51.1858 3.64398 51.7851C3.54242 52.0036 3.43968 52.2215 3.33562 52.4388C2.32577 54.5512 2.17669 56.4547 2.86263 58.7073C3.01576 59.1039 3.20356 59.4568 3.40981 59.8289C3.5225 60.037 3.5225 60.037 3.63746 60.2494C4.87859 62.1326 6.94292 62.8066 8.9398 63.6843C9.31178 63.8522 9.68355 64.0205 10.0551 64.1894C10.9595 64.5991 11.8663 65.0032 12.7753 65.4028C12.8153 65.3166 12.8553 65.2303 12.8965 65.1415C13.8718 63.0391 14.8488 60.9375 15.8274 58.8366C16.3007 57.8206 16.7734 56.8044 17.2449 55.7876C17.656 54.9008 18.0682 54.0145 18.4816 53.1288C18.7002 52.6602 18.9181 52.1914 19.1352 51.7221C19.3783 51.1969 19.6231 50.6726 19.8683 50.1484C19.9744 49.9175 19.9744 49.9175 20.0827 49.6819C21.0673 47.5906 22.3923 46.0275 24.5827 45.1662C26.449 44.5496 28.3612 44.5954 30.1809 45.3663C31.9981 46.3579 33.2552 47.8363 33.9367 49.7843C34.7479 53.0093 32.9216 56.0189 31.5934 58.8335C31.3542 59.3403 31.116 59.8475 30.878 60.3548C30.374 61.4285 29.869 62.5018 29.3637 63.5749C28.7748 64.826 28.1863 66.0773 27.599 67.3291C27.364 67.8294 27.1282 68.3292 26.8923 68.8291C26.7495 69.1332 26.6067 69.4373 26.464 69.7415C26.398 69.8808 26.332 70.02 26.264 70.1635C26.2047 70.2904 26.1454 70.4172 26.0843 70.5479C26.0323 70.6582 25.9803 70.7686 25.9268 70.8823C25.8179 71.1591 25.8179 71.1591 25.8272 71.5049C26.8463 72.0093 27.8679 72.508 28.8929 73.0002C29.369 73.2289 29.8441 73.4594 30.3172 73.6943C30.8633 73.9653 31.4133 74.2281 31.9636 74.4906C32.213 74.6165 32.213 74.6165 32.4674 74.7449C34.2442 75.5765 35.9634 75.378 37.7894 74.7611C39.993 73.8708 41.0652 72.4199 42.0323 70.3206C42.1399 70.0925 42.2477 69.8645 42.3558 69.6366C42.6468 69.0208 42.9342 68.4035 43.2209 67.7857C43.5223 67.1373 43.8264 66.4902 44.1301 65.8429C44.6395 64.756 45.1466 63.6679 45.6523 62.5793C46.2358 61.3232 46.8233 60.0692 47.413 58.816C47.9205 57.7372 48.4257 56.6574 48.9292 55.5768C49.2293 54.9326 49.5302 54.2887 49.833 53.6458C50.1177 53.0411 50.3996 52.4352 50.6795 51.8282C50.7822 51.6066 50.8858 51.3854 50.9906 51.1647C51.5678 49.9462 51.99 48.878 51.9256 47.5073C51.9217 47.3347 51.9178 47.162 51.9137 46.9842C51.7084 45.1142 50.9672 43.326 49.4935 42.1014C49.4217 42.0397 49.35 41.9779 49.2761 41.9143C48.1557 41.0177 46.843 40.4781 45.5487 39.8823C45.2893 39.7616 45.0299 39.6409 44.7706 39.52C44.0923 39.2041 43.4132 38.89 42.7339 38.5762C42.0391 38.2549 41.3451 37.9319 40.6511 37.6092C39.2923 36.9775 37.9327 36.3477 36.5725 35.719C38.2969 33.3394 38.2969 33.3394 40.256 31.1554C40.3787 31.0316 40.5013 30.9078 40.6277 30.7802C44.5955 26.8334 49.3098 22.9933 54.5372 20.8424C54.638 20.8007 54.7388 20.7589 54.8427 20.7159C55.4924 20.4607 56.1141 20.3429 56.8034 20.2478C57.2836 20.0072 57.509 19.8059 57.8146 19.3652C57.9983 18.7147 57.859 18.3698 57.6364 17.7344C57.328 17.3456 57.0465 17.2356 56.5791 17.0694Z" fill="#882A33" />
                              <path d="M44.128 7.62043C43.9618 7.54359 43.7956 7.46675 43.6243 7.38758C42.4068 6.83194 42.4068 6.83194 42.1691 6.91798C40.8007 9.24539 40.3052 11.9963 40.8571 14.6394C40.9292 14.9138 41.0042 15.1874 41.0828 15.46C41.1131 15.5771 41.1434 15.6943 41.1746 15.8151C41.7162 17.6022 43.24 19.6271 44.8805 20.5085C45.0245 20.5769 45.1684 20.6453 45.3167 20.7158C45.4609 20.7856 45.605 20.8554 45.7536 20.9273C46.1535 21.075 46.1535 21.075 46.7056 20.9916C46.9617 20.2346 46.882 19.5134 46.8222 18.7303C46.812 18.5903 46.8019 18.4503 46.7914 18.3061C46.6936 17.1009 46.4852 15.9478 46.1916 14.775C47.3398 16.6276 47.4696 18.5728 47.529 20.6935C47.6679 20.7585 47.8069 20.8235 47.95 20.8904C49.3285 19.2042 50.0895 17.4066 50.0091 15.2031C49.7328 12.5294 48.4609 10.5185 46.4111 8.82582C45.7015 8.31946 44.9217 7.97423 44.128 7.62043Z" fill="#9FB235" />
                              <path d="M41.782 41.3996C41.6387 41.7062 41.6387 41.7062 41.5948 42.1659C42.0216 43.0585 42.676 43.6988 43.4115 44.3386C45.4247 46.1813 46.9678 48.7369 47.5259 51.4274C47.6865 51.4462 47.847 51.4649 48.0125 51.4842C48.7062 49.9211 48.411 48.1067 47.8735 46.5274C46.9393 44.354 44.5104 40.9874 41.782 41.3996Z" fill="#FBF8F8" />
                            </svg>
                          </div>
                        ) : null}
                        <div
                          onClick={() => {
                            setSelectedProId(featuredPro.id);
                            setSessionStep(12);
                          }}
                          className="w-[120px] h-[120px] rounded-full overflow-hidden mb-4 cursor-pointer bg-[#f2eee2] flex items-center justify-center"
                        >
                          {featuredPro.cover_image_preview_url ?? featuredPro.cover_image_url ? (
                            <img src={featuredPro.cover_image_preview_url ?? featuredPro.cover_image_url ?? ''} alt={proDisplayName(featuredPro)} className="w-full h-full object-cover" />
                          ) : (
                            <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[40px]">
                              {proDisplayName(featuredPro).slice(0, 1).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-1">{proDisplayName(featuredPro)}</h3>
                        <div className="flex items-center gap-1 mb-2">
                          <span className="material-icons text-[#8e2a2f] text-sm">star</span>
                          <span className="font-bold text-[#3a3335] text-[12px]">
                            {featuredPro.rating_avg ? Number(featuredPro.rating_avg).toFixed(2) : '—'}
                          </span>
                          <span className="text-[#908e8e] text-[12px]">({featuredPro.rating_count})</span>
                        </div>
                        <p className="text-[#3a3335] text-[14px] mb-6 font-['DM_Sans',sans-serif]">
                          {selectedTimeSlot
                            ? `Free at ${selectedTimeSlot}`
                            : 'Available on this day'}
                        </p>
                        <button
                          onClick={() => {
                            setSelectedProId(featuredPro.id);
                            setSessionStep(12);
                          }}
                          className={`w-full py-3 px-6 rounded-lg font-['DM_Sans',sans-serif] font-bold text-sm transition-all ${selectedProId === featuredPro.id ? 'bg-[#8e2a2f] text-white shadow-md' : 'bg-white text-[#3a3335] border border-[#eee]'}`}
                        >
                          Book with {proDisplayName(featuredPro).split(' ')[0]}
                        </button>
                        {/* See the pro's full profile (about, portfolio,
                            reviews) before committing. New tab so the
                            wizard keeps the customer's place. */}
                        <a
                          href={`/public-profile?id=${featuredPro.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 text-[13px] font-bold text-[#8e2a2f] underline underline-offset-2 hover:text-[#7a2228]"
                        >
                          View profile
                        </a>
                      </div>
                    </div>

                    {/* Browse other pros — secondary option for customers
                        who'd rather not take the recommendation. Reveals the
                        rest of the available pros inline (single-select),
                        keeping the session context (chosen hours, frequency,
                        schedule) intact rather than restarting elsewhere. */}
                    <div className="flex justify-center -mt-10 mb-10 px-4">
                      <button
                        type="button"
                        onClick={() => setShowOtherPros((v) => !v)}
                        className="font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#8e2a2f] underline underline-offset-4 hover:text-[#7a2228] transition-colors"
                      >
                        {showOtherPros ? 'Hide other pros' : 'Browse other pros →'}
                      </button>
                    </div>

                    {showOtherPros ? (
                      otherPros.length > 0 ? (
                      <div className="mb-8">
                        <div className="flex items-center gap-4 mb-8 px-4">
                          <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-sm tracking-[1px] uppercase whitespace-nowrap">
                            Discover other pros
                          </h3>
                          <div className="h-px bg-[#eee] w-full" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12 px-4">
                          {otherPros.map((pro) => {
                            const isSelected = selectedProId === pro.id;
                            const name = proDisplayName(pro);
                            return (
                              <div
                                key={pro.id}
                                onClick={() => setSelectedProId(pro.id)}
                                className={`bg-[#f7f2e8] rounded-lg p-6 flex flex-col items-center text-center cursor-pointer transition-all border-2 ${isSelected ? 'border-[#8e2a2f] ring-2 ring-[#8e2a2f]/10' : 'border-transparent hover:border-[#eee]'}`}
                              >
                                <div className="w-20 h-20 rounded-full overflow-hidden mb-3 bg-[#f2eee2] flex items-center justify-center">
                                  {pro.cover_image_preview_url ?? pro.cover_image_url ? (
                                    <img src={pro.cover_image_preview_url ?? pro.cover_image_url ?? ''} alt={name} className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[28px]">
                                      {name.slice(0, 1).toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <h4 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-1">{name}</h4>
                                <div className="flex items-center gap-1 mb-2">
                                  <span className="material-icons text-[#8e2a2f] text-xs">star</span>
                                  <span className="font-bold text-[#3a3335] text-[12px]">
                                    {pro.rating_avg ? Number(pro.rating_avg).toFixed(2) : '—'}
                                  </span>
                                  <span className="text-[#908e8e] text-[12px]">({pro.rating_count})</span>
                                </div>
                                {/* View the pro's full profile before
                                    committing. Opens in a new tab so the
                                    booking wizard (and its session state)
                                    stays put behind it. */}
                                <a
                                  href={`/public-profile?id=${pro.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[12px] font-bold text-[#8e2a2f] underline underline-offset-2 hover:text-[#7a2228]"
                                >
                                  View profile
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      ) : (
                        <p className="text-center text-sm text-[#908e8e] mb-12 px-4">
                          No other pros are free at your chosen time. Try a
                          different date or time, or go ahead with our
                          recommendation above.
                        </p>
                      )
                    ) : null}
                  </>
                )}
              </div>
            )}

            {sessionStep === 12 && (
              <div className="max-w-[540px] mx-auto w-full px-4">
                {/* Opt-in tick box — the customer confirms whether they're
                    happy for Cherry Picked to assign a suitable alternative
                    if their chosen pro becomes unavailable, or would rather
                    browse and tick their own preferred alternatives. */}
                <label className="flex items-start gap-3 p-5 rounded-lg border-2 border-[#ede8e9] bg-white cursor-pointer hover:border-[#8e2a2f] transition-all">
                  <input
                    type="checkbox"
                    checked={substitutePreference === 'yes'}
                    onChange={(e) =>
                      setSubstitutePreference(e.target.checked ? 'yes' : 'no')
                    }
                    className="mt-0.5 w-5 h-5 accent-[#8e2a2f] cursor-pointer shrink-0"
                  />
                  <span className="font-['DM_Sans',sans-serif] text-[14px] leading-[21px] text-[#3a3335]">
                    <span className="font-bold">
                      I’m happy for Cherry Picked to assign a suitable
                      alternative professional
                    </span>{' '}
                    if my chosen pro becomes unavailable. Leave this unticked
                    if you’d rather choose your own preferred alternatives.
                  </span>
                </label>

                {substitutePreference === 'no' ? (
                  <div className="mt-4 rounded-lg bg-[#f7f2e8] p-5">
                    <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] leading-[21px] mb-3">
                      No problem — you can browse available professionals and
                      tick as many preferred alternatives as you like. We’ll
                      only use them if your chosen pro can’t make it.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        persistDraft();
                        navigate('/secondary-preferred');
                      }}
                      className="inline-flex items-center gap-1 font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#8e2a2f] underline underline-offset-4 hover:text-[#7a2228]"
                    >
                      Browse &amp; choose your preferred alternatives →
                    </button>
                    <p className="mt-3 font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] leading-[18px]">
                      If your chosen professional becomes unavailable and you
                      have not selected a backup or any preferred alternatives,
                      your booking will be cancelled and refunded in full.
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Next Button — disabled until the customer has actually
                picked the thing the current step asks for. Each step's
                gate matches the field rendered above; nothing is
                inferred or auto-filled past this point. */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#eee] md:relative md:p-0 md:bg-transparent md:border-t-0 z-50 mt-5">
              <div className="max-w-[400px] mx-auto">
                <PrimaryButton
                  onClick={handleNext}
                  className="w-full"
                  disabled={!canProceedStep(sessionStep)}
                >
                  Next
                </PrimaryButton>
              </div>
            </div>
          </div>

          {/* Right Sidebar - My Basket */}
          <BookingBasket
            price={selectedServiceData?.price || 0}
            duration={selectedDuration}
            hasPets={hasPets}
            // Only show the pets line when this category's flow actually
            // asks it (cleaning has QUESTION:pets; childcare doesn't).
            // Legacy categories with no step_sequences keep showing it.
            showPets={
              activeSequence ? activeSequence.includes('QUESTION:pets') : true
            }
            // Pass the raw Date so the basket can format it itself — we
            // used to pre-format here, which masked the legacy "Sat 7
            // Mar" mock string sitting underneath.
            appointmentDate={selectedDate}
            appointmentTime={selectedTimeSlot ?? ''}
            selectedTimes={selectedTimeSlot ? [selectedTimeSlot] : []}
            // "Other times you've kept open" is cleaning-only — hide it for
            // hair, beauty & wellness, and childcare.
            isCleaning={flowCategory?.code?.toUpperCase() === 'CLEANING'}
            onSubscribeClick={handleSubscribeClick}
            // Membership upsell shows ONLY for Standard Cleaning (updated
            // requirement). An allowlist on the resolved service code is
            // deliberately strict: a denylist leaked the prompt onto the
            // premium cleans (deep / end-of-tenancy / commercial) AND the
            // service-picker step where no service is resolved yet
            // (resolvedService is null → not in the denylist → shown). With
            // the allowlist, anything that isn't an explicitly-resolved
            // Standard Cleaning hides the upsell.
            allowMembershipUpsell={
              resolvedService?.code === MEMBERSHIP_UPSELL_SERVICE_CODE
            }
            sessionStep={sessionStep}
            selectedAddons={selectedAddons}
            addonsCatalog={addons}
            serviceName={selectedServiceData?.name}
            categoryName={flowCategory?.name}
            commercialSpaceType={isCommercialService ? selectedCommercialSpace : undefined}
            commercialSpaceOptions={commercialSpaceOptions}
            otherCommercialSpace={otherSpaceDetails}
            // Commercial hours (workspace-size recommended/overridden) so the
            // basket bills rate × hours and shows "For Xh", updating live as
            // the size/override changes. Undefined until a size is picked.
            commercialHours={
              isCommercialService && selectedCommercialSize
                ? Math.round((desiredDurationMinutes / 60) * 100) / 100
                : undefined
            }
            pricingUnit={resolvedService?.pricing_unit ?? 'PER_HOUR'}
            isSubscription={isRecurringFreq(frequency)}
            quoteItems={
              resolvedService
                ? [
                    {
                      service_id: resolvedService.id,
                      // PER_HOUR quantity = the chosen hours, so the item
                      // total tracks rate × hours in real time:
                      //   • Commercial: hours come from the workspace-size
                      //     selection (recommended_hours, customer-
                      //     overridable) once a size is picked.
                      //   • Residential: from the step-3 duration picker.
                      // Before those are chosen we can't multiply yet, so
                      // default to 1. PER_BOOKING is always 1. Fractional
                      // hours flow through — priceItems does unitPrice ×
                      // qty without needing an integer.
                      quantity:
                        resolvedService.pricing_unit === 'PER_HOUR'
                          ? (
                              isCommercialService
                                ? !!selectedCommercialSize
                                : sessionStep >= 3
                            )
                            ? Math.max(
                                1 / 60,
                                Math.round(
                                  (desiredDurationMinutes / 60) * 100,
                                ) / 100,
                              )
                            : 1
                          : 1,
                    },
                    // Add-ons only enter the cart on step 10 (the
                    // "Any add-ons?" picker) — earlier steps shouldn't
                    // re-quote them.
                    ...(sessionStep >= 10
                      ? selectedAddons
                          .map((sel) => {
                            const addon = addons.find((a) => a.id === sel.id);
                            if (!addon) return null;
                            const blocks =
                              sel.duration === '1hour'
                                ? Math.max(
                                    1,
                                    Math.round(
                                      60 / (addon.block_minutes || 30),
                                    ),
                                  )
                                : 1;
                            return {
                              service_addon_id: addon.id,
                              quantity: blocks,
                            };
                          })
                          .filter(
                            (
                              x,
                            ): x is {
                              service_addon_id: string;
                              quantity: number;
                            } => !!x,
                          )
                      : []),
                  ]
                : undefined
            }
            walletAvailable={walletBalance}
            walletCurrency={walletCurrency}
            walletAmount={walletAmount}
            walletEnabled={walletEnabled}
            onToggleWallet={(enabled) => {
              setWalletEnabled(enabled);
              if (!enabled) {
                setWalletAmount(0);
                patchBookingDraft({ wallet_amount: 0 });
              }
            }}
            onWalletAmountChange={(n) => {
              setWalletAmount(n);
              setWalletEnabled(n > 0);
              patchBookingDraft({ wallet_amount: n });
            }}
          />
        </div>
      </div>
      <SubscriptionPlanOffcanvas
        isOpen={isSubscriptionPlanOpen}
        onClose={() => setSubscriptionPlanOpen(false)}
        onPickPlan={handlePickPlan}
      />
      <ConfirmModal
        isOpen={signInPromptOpen}
        onClose={() => setSignInPromptOpen(false)}
        onConfirm={() => {
          setSignInPromptOpen(false);
          // Carry the resume target so after sign-in the customer
          // comes straight back to /booking, not /user-landing.
          navigate('/signin/customer', { state: { from: '/booking' } });
        }}
        title="Sign in to subscribe"
        message="Subscription plans live in your account. Sign in (or create a free account) to see the plans available to you — we'll keep your booking-in-progress."
        confirmLabel="Sign in"
        cancelLabel="Maybe later"
      />

      <Footer />
    </div >
  );
}

// ============================================================
// Helpers + AvailabilityCalendar component
// ============================================================

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonthGrid(month: Date): Date {
  // Calendar starts on Monday (matches the existing MON-SUN header).
  // Walk back from the 1st to the previous Monday so the grid is
  // 6 weeks × 7 days = 42 cells.
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // 0 for Mon, 6 for Sun
  first.setDate(first.getDate() - offset);
  return first;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function AvailabilityCalendar({
  month,
  onChangeMonth,
  availableDays,
  selectedDate,
  onSelectDate,
  loading,
  error,
  restrictWeekday,
  allowAnyDate,
}: {
  month: Date;
  onChangeMonth: (d: Date) => void;
  availableDays: Set<string>;
  selectedDate: Date | null;
  onSelectDate: (d: Date) => void;
  loading: boolean;
  error: string | null;
  // Empty string means "no weekday restriction" (one-off booking).
  // When set (e.g. "Tuesday") the calendar greys out any other weekday.
  restrictWeekday?: string;
  // When true, every future date is selectable regardless of the live
  // availability map. Used for booking shapes where a pro is assigned
  // at checkout (recurring, commercial) rather than chosen by slot.
  allowAnyDate?: boolean;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = startOfMonthGrid(month);
  // 6-week grid keeps height consistent month-to-month.
  const cells: Date[] = Array.from({ length: 42 }).map((_, i) => {
    const d = new Date(start.getTime());
    d.setDate(d.getDate() + i);
    return d;
  });

  const monthLabel = month.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  function prevMonth() {
    onChangeMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  }
  function nextMonth() {
    onChangeMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  }

  return (
    <div className="max-w-[420px] mx-auto w-full">
      <div className="border border-[#ede8e9] rounded-[10px] bg-white p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <button
            type="button"
            onClick={prevMonth}
            className="material-icons text-[#3a3335] cursor-pointer hover:text-[#8e2a2f] transition-colors"
          >
            navigate_before
          </button>
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-xl leading-[30px]">
            {monthLabel}
          </h2>
          <button
            type="button"
            onClick={nextMonth}
            className="material-icons text-[#3a3335] cursor-pointer hover:text-[#8e2a2f] transition-colors"
          >
            navigate_next
          </button>
        </div>

        <div className="h-px bg-[#ede8e9] mb-6" />

        <div className="grid grid-cols-7 gap-1 mb-4">
          {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((day) => (
            <div
              key={day}
              className="text-center font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[10px] leading-[15px] tracking-[0.5px]"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-4">
          {cells.map((d) => {
            const inMonth = d.getMonth() === month.getMonth();
            const isPast = d.getTime() < today.getTime();
            const isAvailable = availableDays.has(ymd(d));
            const isSelected = !!selectedDate && isSameDay(d, selectedDate);
            // English long weekday — matches the `id` produced by
            // `weekdayOptions` upstream (also formatted with en-US).
            const cellWeekday = new Intl.DateTimeFormat('en-US', {
              weekday: 'long',
            }).format(d);
            const wrongWeekday =
              !!restrictWeekday && cellWeekday !== restrictWeekday;
            // Recurring / commercial bookings commit to a fixed pro
            // assignment at checkout — let the customer pick any future
            // date even if the live availability map didn't return slots
            // for it. Without this, days like Sunday were always
            // disabled because pros rarely have Sunday slots in the
            // rolling window.
            const ignoreLiveAvailability = !!restrictWeekday || !!allowAnyDate;
            const disabled =
              !inMonth ||
              isPast ||
              wrongWeekday ||
              (!ignoreLiveAvailability && !isAvailable);
            return (
              <button
                key={d.toISOString()}
                type="button"
                disabled={disabled}
                onClick={() => onSelectDate(d)}
                className={`text-center py-2 font-['DM_Sans',sans-serif] font-bold text-base leading-[24px] rounded-lg transition-all ${
                  !inMonth
                    ? 'text-[#cfcccd] cursor-default'
                    : disabled
                      ? 'text-[#cfcccd] cursor-not-allowed'
                      : isSelected
                        ? 'bg-[#8e2a2f] text-white'
                        : 'text-[#3a3335] hover:bg-[#f2eee2]'
                }`}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>

        <div className="mt-6 text-xs text-[#908e8e]">
          {loading ? 'Loading availability…' : null}
          {error ? <span className="text-red-600">{error}</span> : null}
          {!loading &&
          !error &&
          !allowAnyDate &&
          !restrictWeekday &&
          availableDays.size === 0 ? (
            // Only meaningful when live availability actually GATES date
            // selection. When allowAnyDate / restrictWeekday is in play the
            // customer can pick any (matching) date — a pro is assigned at
            // checkout — so an empty availability map isn't a dead-end.
            <span>No pros free this month — try the next one.</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
